/**
 * DocumentValidation.jsx
 * 
 * Phase 1.5 - Document validation and extracted controls viewer
 * Features:
 * - View uploaded documents with validation status
 * - Review extracted controls
 * - Validate or reject parsed data
 * - Check planning readiness
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import './DocumentValidation.css';

const API_BASE = '/api/v1/document-intake';
const CANONICAL_SECTION_OPTIONS = [
  { code: 'SECTION_I', label: "Section 1: Independent Service Auditor's Report" },
  { code: 'SECTION_II', label: 'Section 2: Management Assertion' },
  { code: 'SECTION_III', label: 'Section 3: Description of the System' },
  { code: 'SECTION_IV', label: 'Section 4: Tests of Controls' },
];
const CANONICAL_SECTION_LABELS = CANONICAL_SECTION_OPTIONS.reduce((acc, option) => {
  acc[option.code] = option.label;
  return acc;
}, {});
const ENGAGEMENT_API_BASE = '/api/v1/engagement';
const CANONICAL_SECTION_ORDER = {
  SECTION_I: 1,
  SECTION_II: 2,
  SECTION_III: 3,
  SECTION_IV: 4,
};

const DOCUMENT_TYPES = {
  'SYSTEM_DESCRIPTION': { label: 'System Description', icon: '📄', required: true },
  'CONTROL_LIST': { label: 'Control List', icon: '📋', required: true },
  'PRIOR_REPORT': { label: 'Prior SOC Report', icon: '📊', required: false },
  'POLICY_DOCUMENT': { label: 'Policy Document', icon: '📝', required: false },
  'EVIDENCE': { label: 'Evidence', icon: '📎', required: false },
  'OTHER': { label: 'Other', icon: '📁', required: false },
};

const VALIDATION_STATUS = {
  'DRAFT': { label: 'Draft', color: '#f39c12', icon: '⏳' },
  'VALIDATED': { label: 'Validated', color: '#27ae60', icon: '✅' },
  'REJECTED': { label: 'Rejected', color: '#e74c3c', icon: '❌' },
};
const CONTROL_CORRECTION_REASON_OPTIONS = [
  { code: 'llm_misread_table', label: 'LLM Misread Table' },
  { code: 'wrong_tsc', label: 'Wrong TSC' },
  { code: 'insufficient_evidence', label: 'Insufficient Evidence' },
  { code: 'cross_page_field_split', label: 'Cross-Page Field Split' },
  { code: 'duplicate_ai_row', label: 'Duplicate AI Row' },
  { code: 'template_wording_adjustment', label: 'Template Wording Adjustment' },
];

const MAPPING_CORRECTION_REASON_OPTIONS = [
  { code: 'no_supporting_text', label: 'No Supporting Text in Section III' },
  { code: 'wrong_sentence_selected', label: 'Wrong Sentence Selected' },
  { code: 'missing_supporting_sentences', label: 'Missing Supporting Sentences' },
  { code: 'wrong_control_interpretation', label: 'Wrong Control Interpretation' },
  { code: 'wrong_tsc_context', label: 'Wrong TSC Context Used' },
  { code: 'cross_page_context_split', label: 'Cross-Page Context Split' },
  { code: 'should_be_unmapped', label: 'Should Be Unmapped' },
  { code: 'missed_valid_match', label: 'Missed Valid Match' },
];

const getInitialThemeMode = () => {
  try {
    const saved = localStorage.getItem('document_validation_theme');
    if (saved === 'dark' || saved === 'light') return saved;
  } catch (error) {
    // ignore storage failures
  }
  return 'light';
};

const DocumentValidation = ({ engagementId, userInfo, onBack, onMoveToPlanning }) => {
  const [documents, setDocuments] = useState([]);
  const [extractedControls, setExtractedControls] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [planningReadiness, setPlanningReadiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validationNotes, setValidationNotes] = useState('');
  
  // Upload state
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const [editingSection, setEditingSection] = useState(null);
  const [addingSection, setAddingSection] = useState(null);
  const [editingControl, setEditingControl] = useState(null);
  const [editingMapping, setEditingMapping] = useState(null);
  const [sectionSentenceCatalog, setSectionSentenceCatalog] = useState([]);
  const [sectionSentenceCatalogLoading, setSectionSentenceCatalogLoading] = useState(false);
  const [reprocessing, setReprocessing] = useState(false);
  const [reprocessMessage, setReprocessMessage] = useState('');
  const [controlsStatus, setControlsStatus] = useState({
    in_progress: false,
    controls_count: 0,
    started_at: null,
    last_run_status: null,
    last_run_error: null,
    last_run_completed_at: null
  });
  const statusPollingRef = useRef(null);
  const sectionStatusPollingRef = useRef(null);
  const [expandedControlGroups, setExpandedControlGroups] = useState({});
  const [selectedControlIds, setSelectedControlIds] = useState(new Set());
  const [rerunInProgress, setRerunInProgress] = useState(false);
  const [rerunMessage, setRerunMessage] = useState('');
  const [sectionExtractions, setSectionExtractions] = useState([]);
  const [sectionRuns, setSectionRuns] = useState([]);
  const [sectionExtractionRunning, setSectionExtractionRunning] = useState(false);
  const [sectionExtractionMessage, setSectionExtractionMessage] = useState('');
  const [controlsExpanded, setControlsExpanded] = useState(true);
  const [controlDescriptionMappings, setControlDescriptionMappings] = useState([]);
  const [controlDescriptionExpanded, setControlDescriptionExpanded] = useState(true);
  const [controlDescriptionLoading, setControlDescriptionLoading] = useState(false);
  const [controlDescriptionError, setControlDescriptionError] = useState('');
  const [controlDescriptionMessage, setControlDescriptionMessage] = useState('');
  const [controlDescriptionMappingState, setControlDescriptionMappingState] = useState(null);
  const [controlDescriptionRun, setControlDescriptionRun] = useState(null);
  const [controlDescriptionSummary, setControlDescriptionSummary] = useState({
    processed_controls: 0,
    mapped_controls: 0,
    unmapped_controls: 0,
    failed_controls: 0,
    orphan_reference_controls: 0
  });
  const [controlDescriptionRunning, setControlDescriptionRunning] = useState(false);
  const controlDescriptionPollingRef = useRef(null);
  const intakeV3EventSourceRef = useRef(null);
  const intakeV3StatusPollingRef = useRef(null);
  const intakeV4EventSourceRef = useRef(null);
  const intakeV4StatusPollingRef = useRef(null);
  const [intakeV3RunId, setIntakeV3RunId] = useState(null);
  const [intakeV3Mode, setIntakeV3Mode] = useState(false);
  const [intakeV4Available, setIntakeV4Available] = useState(true);
  const [intakeV4RunId, setIntakeV4RunId] = useState(null);
  const [intakeV4Run, setIntakeV4Run] = useState(null);
  const [intakeV4Running, setIntakeV4Running] = useState(false);
  const [intakeV4Loading, setIntakeV4Loading] = useState(false);
  const [intakeV4Message, setIntakeV4Message] = useState('');
  const [intakeV4Error, setIntakeV4Error] = useState('');
  const [intakeV4Escalations, setIntakeV4Escalations] = useState([]);
  const [intakeV4BatchProgress, setIntakeV4BatchProgress] = useState({
    attemptNo: null,
    method: null,
    total: 0,
    completed: 0,
    failed: 0,
    inProgress: 0,
    nextAttemptNo: null,
    nextMethod: null,
    statusByBatch: {}
  });
  const [personnelEntries, setPersonnelEntries] = useState([]);
  const [personnelSummary, setPersonnelSummary] = useState(null);
  const [personnelLoading, setPersonnelLoading] = useState(false);
  const [personnelError, setPersonnelError] = useState('');
  const [themeMode, setThemeMode] = useState(getInitialThemeMode);
  const [transitioning, setTransitioning] = useState(false);
  const [newPersonnel, setNewPersonnel] = useState({
    full_name: '',
    position_title: '',
    page_start: '',
    page_end: '',
    org_unit: ''
  });
  const [editingPersonnelId, setEditingPersonnelId] = useState(null);
  const [editingPersonnel, setEditingPersonnel] = useState({
    full_name: '',
    position_title: '',
    page_start: '',
    page_end: '',
    org_unit: ''
  });
  const intakeV3MappingRefreshInFlightRef = useRef(false);
  const intakeV3MappingRefreshQueuedRef = useRef(false);

  const sortedControlDescriptionMappings = useMemo(() => {
    const parseControlId = (raw) => {
      const value = String(raw || '').trim();
      const match = value.match(/^([A-Za-z]+)?(\d+(?:\.\d+)*)$/);
      if (!match) {
        return { prefix: 'ZZZ', nums: [Number.MAX_SAFE_INTEGER], raw: value };
      }
      const prefix = (match[1] || '').toUpperCase();
      const nums = match[2].split('.').map((n) => Number(n));
      return { prefix, nums, raw: value };
    };

    const compareParsed = (a, b) => {
      if (a.prefix !== b.prefix) return a.prefix.localeCompare(b.prefix);
      const len = Math.max(a.nums.length, b.nums.length);
      for (let i = 0; i < len; i += 1) {
        const av = Number.isFinite(a.nums[i]) ? a.nums[i] : -1;
        const bv = Number.isFinite(b.nums[i]) ? b.nums[i] : -1;
        if (av !== bv) return av - bv;
      }
      return a.raw.localeCompare(b.raw, undefined, { numeric: true, sensitivity: 'base' });
    };

    return [...(controlDescriptionMappings || [])].sort((left, right) => {
      const leftId = left.internal_control_id_final || left.internal_control_id_ai || '';
      const rightId = right.internal_control_id_final || right.internal_control_id_ai || '';
      const idCmp = compareParsed(parseControlId(leftId), parseControlId(rightId));
      if (idCmp !== 0) return idCmp;
      const leftPage = Number(left.page_number_final ?? left.page_number_ai ?? Number.MAX_SAFE_INTEGER);
      const rightPage = Number(right.page_number_final ?? right.page_number_ai ?? Number.MAX_SAFE_INTEGER);
      if (leftPage !== rightPage) return leftPage - rightPage;
      const leftParagraph = Number(left.paragraph_index_final ?? left.paragraph_index_ai ?? Number.MAX_SAFE_INTEGER);
      const rightParagraph = Number(right.paragraph_index_final ?? right.paragraph_index_ai ?? Number.MAX_SAFE_INTEGER);
      return leftParagraph - rightParagraph;
    });
  }, [controlDescriptionMappings]);

  const unmappedControlDescriptionCount = useMemo(
    () =>
      (controlDescriptionMappings || []).filter((row) => {
        if (typeof row.is_mapped === 'boolean') return !row.is_mapped;
        const paragraphId = row.paragraph_id_final ?? row.paragraph_id_ai;
        const mappedText = (row.mapped_paragraph_text_final ?? row.mapped_paragraph_text_ai ?? '').trim();
        return !paragraphId || !mappedText;
      }).length,
    [controlDescriptionMappings]
  );

  const intakeV4Summary = useMemo(
    () => intakeV4Run?.summary_json || {},
    [intakeV4Run]
  );

  const intakeV4StageStatuses = useMemo(
    () => ([
      { label: 'Extraction', value: intakeV4Summary.extraction_status || null },
      { label: 'Mapping', value: intakeV4Summary.mapping_status || null },
      { label: 'Validation', value: intakeV4Summary.validator_status || null },
      { label: 'Final', value: intakeV4Run?.status || null },
    ]),
    [intakeV4Summary, intakeV4Run]
  );

  const sectionDataById = useMemo(() => {
    const map = new Map();
    (sectionExtractions || []).forEach((section) => {
      if (section?.section_id) {
        const rawFlags = section?.flags_json;
        let flags = rawFlags;
        if (typeof rawFlags === 'string') {
          try {
            flags = JSON.parse(rawFlags);
          } catch (error) {
            flags = {};
          }
        }
        const tables = Array.isArray(flags?.tables) ? flags.tables : [];
        map.set(String(section.section_id), {
          rawText: section.raw_text || '',
          tables
        });
      }
    });
    return map;
  }, [sectionExtractions]);

  const latestSectionRuns = useMemo(() => {
    const latest = new Map();
    (sectionRuns || []).forEach((run) => {
      const key = run.section_id || run.run_type;
      if (!latest.has(key)) {
        latest.set(key, run);
        return;
      }
      const current = latest.get(key);
      const currentTime = current?.started_at ? Date.parse(current.started_at) : 0;
      const runTime = run?.started_at ? Date.parse(run.started_at) : 0;
      if (runTime >= currentTime) {
        latest.set(key, run);
      }
    });
    return Array.from(latest.values()).sort((a, b) => {
      const aPage = Number(a.page_start || 0);
      const bPage = Number(b.page_start || 0);
      return aPage - bPage;
    });
  }, [sectionRuns]);

  const mappingSentenceOptions = useMemo(
    () => [...(sectionSentenceCatalog || [])].sort((left, right) => {
      const leftPage = Number(left.page_number || Number.MAX_SAFE_INTEGER);
      const rightPage = Number(right.page_number || Number.MAX_SAFE_INTEGER);
      if (leftPage !== rightPage) return leftPage - rightPage;
      return Number(left.sentence_index || 0) - Number(right.sentence_index || 0);
    }),
    [sectionSentenceCatalog]
  );

  const [expandedPanels, setExpandedPanels] = useState({
    sectionI: true,
    sectionIReview: false,
    sectionII: true,
    sectionIIReview: false,
    sectionIII: true,
    sectionIIIReview: false,
    sectionIV: true,
    mapping: true,
    personnel: true,
    escalations: true,
  });

  useEffect(() => {
    try {
      localStorage.setItem('document_validation_theme', themeMode);
    } catch (error) {
      // ignore storage failures
    }
  }, [themeMode]);

  useEffect(() => {
    setExpandedPanels({
      sectionI: true,
      sectionIReview: false,
      sectionII: true,
      sectionIIReview: false,
      sectionIII: true,
      sectionIIIReview: false,
      sectionIV: true,
      mapping: true,
      personnel: true,
      escalations: true,
    });
  }, [selectedDocument?.id]);

  const togglePanel = (panelKey) => {
    setExpandedPanels((prev) => ({
      ...prev,
      [panelKey]: !prev[panelKey],
    }));
  };

  const statusPillClassName = (value) => {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'pass' || normalized === 'completed' || normalized === 'success') {
      return 'success';
    }
    if (normalized === 'fail' || normalized === 'failed' || normalized === 'error') {
      return 'failed';
    }
    if (normalized === 'running' || normalized === 'in_progress' || normalized === 'in-progress') {
      return 'running';
    }
    if (normalized === 'warning' || normalized === 'warn') {
      return 'warning';
    }
    return normalized || 'idle';
  };

  const resetIntakeV4BatchProgress = () => {
    setIntakeV4BatchProgress({
      attemptNo: null,
      method: null,
      total: 0,
      completed: 0,
      failed: 0,
      inProgress: 0,
      nextAttemptNo: null,
      nextMethod: null,
      statusByBatch: {}
    });
  };

  const parsePositiveInt = (value, fallback = null) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return fallback;
    return Math.floor(n);
  };

  const applyIntakeV4BatchEvent = (eventType, payload) => {
    if (String(payload?.section || '').toUpperCase() !== 'SECTION_III') {
      return;
    }
    setIntakeV4BatchProgress((prev) => {
      const attemptNo = parsePositiveInt(payload?.attempt_no, prev.attemptNo || 1);
      const totalFromPayload = parsePositiveInt(payload?.batch_total, prev.total || 0);
      const method = payload?.method || prev.method || null;
      const nextAttemptNo = parsePositiveInt(payload?.next_attempt_no, null);
      const nextMethod = payload?.next_method || null;
      const isAttemptShift = prev.attemptNo && attemptNo && attemptNo !== prev.attemptNo;
      const statusByBatch = isAttemptShift ? {} : { ...(prev.statusByBatch || {}) };
      const batchIndex = parsePositiveInt(payload?.batch_index, null);
      const batchKey = batchIndex ? String(batchIndex) : null;

      if (eventType === 'batch_started' && batchKey) {
        if (!['completed', 'failed'].includes(String(statusByBatch[batchKey] || ''))) {
          statusByBatch[batchKey] = 'in_progress';
        }
      } else if (eventType === 'batch_completed' && batchKey) {
        statusByBatch[batchKey] = 'completed';
      } else if (eventType === 'batch_failed' && batchKey) {
        statusByBatch[batchKey] = 'failed';
      }

      const statuses = Object.values(statusByBatch);
      const completed = statuses.filter((s) => s === 'completed').length;
      const failed = statuses.filter((s) => s === 'failed').length;
      const inProgress = statuses.filter((s) => s === 'in_progress').length;
      const total = Math.max(totalFromPayload || 0, prev.total || 0, completed + failed + inProgress);

      return {
        attemptNo,
        method,
        total,
        completed,
        failed,
        inProgress,
        nextAttemptNo: eventType === 'batch_retry_scheduled' ? nextAttemptNo : null,
        nextMethod: eventType === 'batch_retry_scheduled' ? nextMethod : null,
        statusByBatch,
      };
    });
  };

  const handleIntakeV4ItemCompleted = (documentId, runExecutionId, payload) => {
    const stage = String(payload?.stage || '').toLowerCase();
    const itemId = String(payload?.item_id || '').trim();
    const status = String(payload?.status || '').toLowerCase();
    const method = payload?.method ? ` via ${payload.method}` : '';

    if (stage === 'mapping') {
      const itemLabel = itemId || 'control';
      setIntakeV4Message(`Intake v4 mapping updated: ${itemLabel} ${status || 'completed'}${method}.`);
      loadIntakeV4MappingRows(documentId, runExecutionId, true);
      loadExtractedControls(documentId);
      fetchIntakeV4RunStatus(documentId, true);
      return;
    }

    if (stage === 'extraction') {
      const itemLabel = itemId || 'section';
      setIntakeV4Message(`Intake v4 extraction updated: ${itemLabel} ${status || 'completed'}${method}.`);
      fetchIntakeV4RunStatus(documentId, true);
      const sectionCode = String(payload?.section || '').toUpperCase();
      if (['SECTION_I', 'SECTION_II', 'SECTION_III'].includes(sectionCode)) {
        refreshSectionExtractionViews(documentId, true);
      }
      if (sectionCode === 'SECTION_IV') {
        loadExtractedControls(documentId);
      }
    }
  };

  const canonicalSectionHeadingByCode = useMemo(() => {
    const map = new Map();
    (selectedDocument?.section_headings || []).forEach((section) => {
      const code = String(section?.canonical_section_code || '').trim().toUpperCase();
      if (code && !map.has(code)) {
        map.set(code, section);
      }
    });
    return map;
  }, [selectedDocument]);

  const orderedSectionWorkflowCodes = useMemo(() => {
    const rows = (selectedDocument?.section_headings || [])
      .filter((section) => ['SECTION_I', 'SECTION_II', 'SECTION_III'].includes(String(section?.canonical_section_code || '').trim().toUpperCase()))
      .sort((a, b) => {
        const aPage = Number(a?.page_start || 0);
        const bPage = Number(b?.page_start || 0);
        if (aPage !== bPage) return aPage - bPage;
        return String(a?.display_title || a?.heading || '').localeCompare(String(b?.display_title || b?.heading || ''), undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      })
      .map((section) => String(section?.canonical_section_code || '').trim().toUpperCase());
    return rows.length ? rows : ['SECTION_I', 'SECTION_II', 'SECTION_III'];
  }, [selectedDocument]);

  const groupedSectionReviewItems = useMemo(() => {
    const grouped = {
      SECTION_I: [],
      SECTION_II: [],
      SECTION_III: [],
    };
    const headingCodeById = new Map();
    (selectedDocument?.section_headings || []).forEach((section) => {
      if (section?.id) {
        headingCodeById.set(String(section.id), String(section.canonical_section_code || '').trim().toUpperCase());
      }
    });
    (sectionExtractions || []).forEach((section) => {
      const codeFromId = headingCodeById.get(String(section?.section_id || '')) || String(section?.run_type || '').trim().toUpperCase();
      const review = Array.isArray(section?.review_items_json) ? section.review_items_json : [];
      review.forEach((item) => {
        if (grouped[codeFromId]) {
          grouped[codeFromId].push({
            page: item.page,
            flags: item.flags || [],
            section_heading: section.section_heading,
          });
        }
      });
    });
    Object.values(grouped).forEach((items) => items.sort((a, b) => Number(a.page || 0) - Number(b.page || 0)));
    return grouped;
  }, [sectionExtractions, selectedDocument]);

  const latestSectionRunByCode = useMemo(() => {
    const map = new Map();
    latestSectionRuns.forEach((run) => {
      const code = String(run?.run_type || '').trim().toUpperCase();
      if (code) {
        map.set(code, run);
      }
    });
    return map;
  }, [latestSectionRuns]);

  const sectionAIInProgress = useMemo(
    () => (sectionRuns || []).some((run) => run.status === 'running'),
    [sectionRuns]
  );

  const loadDocumentDetails = React.useCallback(async (documentId) => {
    if (!documentId) return null;
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId,
      });
      const response = await fetch(`${API_BASE}/document/${documentId}?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      const sectionHeadings = data.section_headings || [];
      setDocuments((prev) => prev.map((doc) => (
        doc.id === documentId ? { ...doc, section_headings: sectionHeadings } : doc
      )));
      setSelectedDocument((prev) => (
        prev && prev.id === documentId ? { ...prev, section_headings: sectionHeadings } : prev
      ));
      return data;
    } catch (err) {
      console.error('Error loading document details:', err);
      return null;
    }
  }, [engagementId, userInfo?.user_id]);

  const loadDocumentsCallback = React.useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/engagements/${engagementId}/documents`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        const docs = data.documents || [];
        setDocuments(docs);
        // Preserve current selection if the document still exists
        setSelectedDocument(prev => {
          if (!prev) return null;
          const next = docs.find(d => d.id === prev.id) || null;
          return next ? { ...next, section_headings: prev.section_headings || next.section_headings || [] } : null;
        });
      } else {
        // Demo data
        setDocuments([
          {
            id: 'doc-1',
            filename: 'System_Description_2025.pdf',
            document_type: 'SYSTEM_DESCRIPTION',
            validation_status: 'DRAFT',
            file_checksum: 'sha256:abc123...',
            created_at: new Date().toISOString(),
            file_size: 1024000,
          },
          {
            id: 'doc-2',
            filename: 'Control_Matrix.xlsx',
            document_type: 'CONTROL_LIST',
            validation_status: 'VALIDATED',
            validated_by: 'John Auditor',
            validated_at: new Date().toISOString(),
            file_checksum: 'sha256:def456...',
            created_at: new Date().toISOString(),
            file_size: 256000,
          },
        ]);
      }
    } catch (err) {
      console.error('Error loading documents:', err);
      setError('Failed to load documents');
    } finally {
      setLoading(false);
    }
  }, [engagementId]);

  const checkPlanningReadinessCallback = React.useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/engagements/${engagementId}/planning-readiness`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Map backend response to frontend format
        setPlanningReadiness({
          ready: data.ready,
          missing_documents: data.details?.missing || [],
          validated_count: data.details?.validated?.length || 0,
          required_count: data.details?.required?.length || 2,
        });
      } else {
        // Demo data
        setPlanningReadiness({
          ready: false,
          missing_documents: ['SYSTEM_DESCRIPTION'],
          validated_count: 1,
          required_count: 2,
        });
      }
    } catch (err) {
      console.error('Error checking readiness:', err);
    }
  }, [engagementId]);

  const handleMoveToPlanning = async () => {
    if (!engagementId || transitioning) return;
    setError(null);
    setTransitioning(true);
    try {
      const response = await fetch(`${ENGAGEMENT_API_BASE}/engagements/${engagementId}/transition`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: JSON.stringify({
          to_status: 'PLANNING',
          reason: null,
        }),
      });

      if (response.ok) {
        checkPlanningReadinessCallback();
        if (onMoveToPlanning) {
          onMoveToPlanning();
        }
      } else {
        const err = await response.json();
        setError(err.detail || 'Failed to move to Planning');
      }
    } catch (err) {
      setError('Failed to move to Planning: ' + err.message);
    } finally {
      setTransitioning(false);
    }
  };

  useEffect(() => {
    loadDocumentsCallback();
    checkPlanningReadinessCallback();
  }, [loadDocumentsCallback, checkPlanningReadinessCallback]);

  useEffect(() => {
    setEditingSection(null);
    setAddingSection(null);
    setEditingControl(null);
    setReprocessMessage('');
    setSectionExtractionMessage('');
    setSelectedControlIds(new Set());
    setRerunMessage('');
    setControlDescriptionError('');
    setControlDescriptionMessage('');
    setControlDescriptionMappingState(null);
    setControlDescriptionRun(null);
    setControlDescriptionSummary({
      processed_controls: 0,
      mapped_controls: 0,
      unmapped_controls: 0
    });
    setControlDescriptionRunning(false);
    setIntakeV3RunId(null);
    setIntakeV3Mode(false);
    setIntakeV4Available(true);
    setIntakeV4RunId(null);
    setIntakeV4Run(null);
    setIntakeV4Running(false);
    setIntakeV4Loading(false);
    setIntakeV4Message('');
    setIntakeV4Error('');
    setIntakeV4Escalations([]);
    resetIntakeV4BatchProgress();
    setPersonnelError('');
    setEditingPersonnelId(null);
    setControlsExpanded(true);
  }, [selectedDocument]);

  useEffect(() => {
    if (selectedDocument) {
      if (!selectedDocument.section_headings || selectedDocument.section_headings.length === 0) {
        loadDocumentDetails(selectedDocument.id);
      }
      loadExtractedControls(selectedDocument.id);
      fetchControlsStatus(selectedDocument.id, false);
      loadSectionExtractions(selectedDocument.id);
      loadPersonnelEntries(selectedDocument.id);
      fetchSectionExtractionStatus(selectedDocument.id, false).then((status) => {
        const runs = status?.runs || [];
        if (runs.some((run) => run.status === 'running')) {
          startSectionStatusPolling(selectedDocument.id);
        }
      });
      let cancelled = false;
      (async () => {
        const run = await fetchIntakeV3RunStatus(selectedDocument.id);
        if (cancelled) return;
        if (run?.run_id) {
          setIntakeV3Mode(true);
          setIntakeV3RunId(run.run_id);
          setControlDescriptionRun(run);
          await loadControlSentenceMappingV3(selectedDocument.id, false);
          const status = String(run.status || '').toLowerCase();
          if (status === 'running') {
            setControlDescriptionRunning(true);
            setControlDescriptionMessage('Intake v3 is running...');
            startIntakeV3EventStream(selectedDocument.id, run.run_id);
            return;
          }
          setControlDescriptionRunning(false);
          if (status === 'completed') {
            setControlDescriptionMessage('Intake v3 completed.');
            return;
          }
          if (status === 'failed') {
            setControlDescriptionError(run.error || 'Intake v3 failed.');
            return;
          }
        }
        setIntakeV3Mode(false);
        setIntakeV3RunId(null);
        loadControlSentenceMappingV3(selectedDocument.id, false).then((sentenceData) => {
          if ((sentenceData?.count || 0) > 0) return sentenceData;
          return loadControlDescriptionMapping(selectedDocument.id, false);
        }).then((data) => {
          const running = (data?.run?.status || '').toLowerCase() === 'running';
          if (running) {
            startControlDescriptionPolling(selectedDocument.id);
          }
        });
      })();
      (async () => {
        const run = await fetchIntakeV4RunStatus(selectedDocument.id, true);
        if (cancelled) return;
        if (!run) {
          setIntakeV4Run(null);
          setIntakeV4RunId(null);
          setIntakeV4Escalations([]);
          setIntakeV4Running(false);
          return;
        }
        const runExecutionId = run.run_execution_id;
        const status = String(run.status || '').toLowerCase();
        const mappingStatus = String(run.summary_json?.mapping_status || '').toLowerCase();
        setIntakeV4Run(run);
        setIntakeV4RunId(runExecutionId || null);
        if (runExecutionId && status !== 'fail' && mappingStatus !== 'skipped') {
          await loadIntakeV4Escalations(selectedDocument.id, runExecutionId, true);
          await loadIntakeV4MappingRows(selectedDocument.id, runExecutionId, true);
        }
        if (status === 'running' || status === 'in_progress') {
          setIntakeV4Running(true);
          setIntakeV4Message('Intake v4 is running...');
          if (runExecutionId) {
            startIntakeV4EventStream(selectedDocument.id, runExecutionId);
          } else {
            startIntakeV4StatusPolling(selectedDocument.id);
          }
          return;
        }
        setIntakeV4Running(false);
        if (status === 'pass' || status === 'warning') {
          setIntakeV4Message(`Intake v4 completed with status: ${status}.`);
        } else if (status === 'fail') {
          setIntakeV4Error(run.error || 'Intake v4 failed.');
        }
      })();
      return () => {
        cancelled = true;
        stopStatusPolling();
        stopSectionStatusPolling();
        stopControlDescriptionPolling();
        stopIntakeV3EventSource();
        stopIntakeV3StatusPolling();
        stopIntakeV4EventSource();
        stopIntakeV4StatusPolling();
      };
    }
    return undefined;
  }, [selectedDocument, loadDocumentDetails]);

  const stopStatusPolling = () => {
    if (statusPollingRef.current) {
      clearInterval(statusPollingRef.current);
      statusPollingRef.current = null;
    }
  };

  const stopSectionStatusPolling = () => {
    if (sectionStatusPollingRef.current) {
      clearInterval(sectionStatusPollingRef.current);
      sectionStatusPollingRef.current = null;
    }
  };

  const stopControlDescriptionPolling = () => {
    if (controlDescriptionPollingRef.current) {
      clearInterval(controlDescriptionPollingRef.current);
      controlDescriptionPollingRef.current = null;
    }
  };

  const stopIntakeV3EventSource = () => {
    if (intakeV3EventSourceRef.current) {
      intakeV3EventSourceRef.current.close();
      intakeV3EventSourceRef.current = null;
    }
  };

  const stopIntakeV3StatusPolling = () => {
    if (intakeV3StatusPollingRef.current) {
      clearInterval(intakeV3StatusPollingRef.current);
      intakeV3StatusPollingRef.current = null;
    }
  };

  const stopIntakeV4EventSource = () => {
    if (intakeV4EventSourceRef.current) {
      intakeV4EventSourceRef.current.close();
      intakeV4EventSourceRef.current = null;
    }
  };

  const stopIntakeV4StatusPolling = () => {
    if (intakeV4StatusPollingRef.current) {
      clearInterval(intakeV4StatusPollingRef.current);
      intakeV4StatusPollingRef.current = null;
    }
  };

  const fetchControlsStatus = async (documentId, silent = false) => {
    if (!documentId) return null;
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId
      });
      const resp = await fetch(`${API_BASE}/documents/${documentId}/controls-status?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!resp.ok) {
        return null;
      }
      const data = await resp.json();
      setControlsStatus(data);
      if (!silent && data.in_progress) {
        const startedAt = data.started_at ? new Date(data.started_at).toLocaleString() : 'now';
        setReprocessMessage(`Control detection in progress (started ${startedAt}).`);
      }
      return data;
    } catch (e) {
      if (!silent) {
        console.error('Failed to fetch control status', e);
      }
      return null;
    }
  };

  const fetchSectionExtractionStatus = async (documentId, silent = false) => {
    if (!documentId) return null;
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId
      });
      const resp = await fetch(`${API_BASE}/documents/${documentId}/section-extractions/status?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!resp.ok) {
        return null;
      }
      const data = await resp.json();
      setSectionRuns(data.runs || []);
      if (!silent && (data.runs || []).some((run) => run.status === 'running')) {
        setSectionExtractionMessage('Section I–III extraction in progress. This may take a few minutes...');
      }
      return data;
    } catch (e) {
      if (!silent) {
        console.error('Failed to fetch section extraction status', e);
      }
      return null;
    }
  };

  const startStatusPolling = (documentId) => {
    stopStatusPolling();
    statusPollingRef.current = setInterval(async () => {
      const status = await fetchControlsStatus(documentId, true);
      if (status && !status.in_progress) {
        stopStatusPolling();
        setReprocessMessage('Control detection completed. Refreshing results...');
        loadDocumentsCallback();
        loadExtractedControls(documentId);
        loadControlSentenceMappingV3(documentId, true).then((sentenceData) => {
          if ((sentenceData?.count || 0) > 0) return sentenceData;
          return loadControlDescriptionMapping(documentId, true);
        }).then((data) => {
          const mappingStatus = String(data?.mapping_state?.status || data?.run?.status || '').toLowerCase();
          if (mappingStatus === 'running') {
            startControlDescriptionPolling(documentId);
          }
        });
      }
    }, 8000);
  };

  const startSectionStatusPolling = (documentId) => {
    stopSectionStatusPolling();
    sectionStatusPollingRef.current = setInterval(async () => {
      const status = await fetchSectionExtractionStatus(documentId, true);
      const runs = status?.runs || [];
      const stillRunning = runs.some((run) => run.status === 'running');
      if (!stillRunning) {
        stopSectionStatusPolling();
        setSectionExtractionMessage('Section I–III extraction completed. Refreshing results...');
        loadSectionExtractions(documentId);
        loadPersonnelEntries(documentId);
        loadControlSentenceMappingV3(documentId, true).then((sentenceData) => {
          if ((sentenceData?.count || 0) > 0) return sentenceData;
          return loadControlDescriptionMapping(documentId, true);
        }).then((data) => {
          const mappingStatus = String(data?.mapping_state?.status || data?.run?.status || '').toLowerCase();
          if (mappingStatus === 'running') {
            startControlDescriptionPolling(documentId);
          }
        });
      }
    }, 10000);
  };

  const refreshSectionExtractionViews = async (documentId, silent = true) => {
    if (!documentId) return;
    await fetchSectionExtractionStatus(documentId, silent);
    await loadSectionExtractions(documentId);
  };

  const loadExtractedControls = async (documentId) => {
    try {
      const params = new URLSearchParams({ engagement_id: engagementId });
      const response = await fetch(`${API_BASE}/documents/${documentId}/controls?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setExtractedControls(data.controls || []);
      } else {
        // Demo data
        setExtractedControls([
          {
            id: 'ctrl-1',
            control_id: 'CTRL-001',
            description: 'Access to the system requires multi-factor authentication for all users.',
            control_nature: 'AUTOMATED',
            control_type: 'PREVENTIVE',
            frequency: 'continuous',
            source_page: 12,
          },
          {
            id: 'ctrl-2',
            control_id: 'CTRL-002',
            description: 'Security logs are reviewed daily by the Security Manager for anomalies.',
            control_nature: 'MANUAL',
            control_type: 'DETECTIVE',
            frequency: 'daily',
            source_page: 15,
          },
          {
            id: 'ctrl-3',
            control_id: 'CTRL-003',
            description: 'Quarterly access reviews are performed to ensure appropriate access levels.',
            control_nature: 'MANUAL',
            control_type: 'DETECTIVE',
            frequency: 'quarterly',
            source_page: 18,
          },
        ]);
      }
    } catch (err) {
      console.error('Error loading controls:', err);
    }
  };

  const loadSectionExtractions = async (documentId) => {
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId
      });
      const response = await fetch(`${API_BASE}/documents/${documentId}/section-extractions?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (response.ok) {
        const data = await response.json();
        setSectionExtractions(data.sections || []);
      }
    } catch (err) {
      console.error('Error loading section extractions:', err);
    }
  };

  const loadControlDescriptionMapping = async (documentId, silent = true) => {
    if (!documentId) return null;
    if (!silent) setControlDescriptionLoading(true);
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId
      });
      const response = await fetch(`${API_BASE}/documents/${documentId}/control-description-mapping?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load control-description mapping');
      }
      const data = await response.json();
      setControlDescriptionMappingState(data.mapping_state || null);
      setControlDescriptionMappings(data.entries || []);
      setControlDescriptionRun(data.run || null);
      setControlDescriptionSummary(data.summary || {
        processed_controls: (data.entries || []).length,
        mapped_controls: (data.entries || []).filter((row) => !!row.is_mapped).length,
        unmapped_controls: (data.entries || []).filter((row) => !row.is_mapped).length,
        failed_controls: 0,
        orphan_reference_controls: 0
      });
      const running = (data.run?.status || '').toLowerCase() === 'running';
      const mappingStateStatus = String(data.mapping_state?.status || '').toLowerCase();
      const mappingRunning = running || mappingStateStatus === 'running';
      setControlDescriptionRunning(mappingRunning);
      if (!running && ['stale', 'pending', 'blocked', 'failed'].includes(mappingStateStatus)) {
        setControlDescriptionMessage(data.mapping_state?.reason_detail || `Mapping status: ${mappingStateStatus}`);
      }
      if (!mappingRunning) {
        stopControlDescriptionPolling();
      }
      return data;
    } catch (err) {
      if (!silent) {
        console.error('Error loading control-description mapping:', err);
        setControlDescriptionError('Failed to load control-description mapping.');
      }
      return null;
    } finally {
      if (!silent) setControlDescriptionLoading(false);
    }
  };

  const loadControlSentenceMappingV3 = async (documentId, silent = true) => {
    if (!documentId) return null;
    if (!silent) setControlDescriptionLoading(true);
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId
      });
      const response = await fetch(`${API_BASE}/documents/${documentId}/control-sentence-mapping?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      const rows = (data.entries || []).map((row) => ({
        ...row,
        id: row.id || `${row.internal_control_id_final || row.internal_control_id_ai || 'unknown'}-${row.primary_sentence_id_final || row.primary_sentence_id_ai || 'none'}`,
        internal_control_id_final: row.internal_control_id_final || row.internal_control_id_ai || '',
        internal_control_statement_final: row.internal_control_statement_final || row.internal_control_statement_ai || '',
        paragraph_id_final: row.primary_sentence_id_final || row.primary_sentence_id_ai || null,
        page_number_final: row.page_number_primary_final ?? row.page_number_primary_ai ?? null,
        paragraph_index_final: null,
        mapped_paragraph_text_final: row.mapped_text_final ?? row.mapped_text_ai ?? null,
        relevance_score_ai: row.relevance_score_final ?? row.relevance_score_ai ?? null,
      }));
      const mapped = rows.filter((row) => !!row.is_mapped).length;
      const unmapped = Math.max(0, rows.length - mapped);
      setControlDescriptionMappings(rows);
      setControlDescriptionMappingState(rows.length ? { status: 'current' } : null);
      setControlDescriptionSummary({
        processed_controls: rows.length,
        mapped_controls: mapped,
        unmapped_controls: unmapped,
        failed_controls: 0,
        orphan_reference_controls: 0
      });
      return { entries: rows, count: rows.length, mapping_state: rows.length ? { status: 'current' } : null };
    } catch (err) {
      if (!silent) {
        console.error('Error loading control-sentence mapping v3:', err);
      }
      return null;
    } finally {
      if (!silent) setControlDescriptionLoading(false);
    }
  };

  const waitForEditedControlMapping = async (documentId, editedControlRowId) => {
    const maxAttempts = 15;
    const delayMs = 2000;
    for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
      await new Promise((resolve) => window.setTimeout(resolve, delayMs));
      const data = await loadControlSentenceMappingV3(documentId, true);
      const rows = data?.entries || [];
      const rowFound = rows.some((row) =>
        String(row.extracted_control_row_id || '').trim() === String(editedControlRowId || '').trim()
      );
      if (rowFound) {
        setControlDescriptionMessage('Mapping refresh completed for edited control.');
        return true;
      }
    }
    setControlDescriptionMessage('Mapping refresh is still running. Refresh mapping again in a few seconds.');
    return false;
  };

  const loadSectionIIISentences = async (documentId) => {
    if (!documentId) return [];
    setSectionSentenceCatalogLoading(true);
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId
      });
      const response = await fetch(`${API_BASE}/documents/${documentId}/section-iii-sentences?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load Section III sentences');
      }
      const data = await response.json();
      const entries = data.entries || [];
      setSectionSentenceCatalog(entries);
      return entries;
    } catch (err) {
      console.error('Error loading Section III sentences:', err);
      setSectionSentenceCatalog([]);
      return [];
    } finally {
      setSectionSentenceCatalogLoading(false);
    }
  };

  const loadIntakeV4MappingRows = async (documentId, runExecutionId = null, silent = true) => {
    if (!documentId) return null;
    const runIdToUse = runExecutionId || intakeV4RunId;
    if (!runIdToUse) return null;
    if (!silent) setControlDescriptionLoading(true);
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId,
        run_execution_id: runIdToUse
      });
      const response = await fetch(`${API_BASE}/documents/${documentId}/intake-v4/mapping-rows?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!response.ok) {
        return null;
      }
      const data = await response.json();
      setControlDescriptionMappingState(data.mapping_state || null);
      if (data.current === false) {
        setControlDescriptionSummary(data.summary || {
          processed_controls: 0,
          mapped_controls: 0,
          unmapped_controls: 0,
          failed_controls: 0,
          orphan_reference_controls: 0
        });
        return data;
      }
      const rows = data?.entries || [];
      const summary = data?.summary || {
        processed_controls: rows.length,
        mapped_controls: rows.filter((row) => !!row.is_mapped).length,
        unmapped_controls: rows.filter((row) => String(row.mapping_status || '').toLowerCase() === 'not_mapped').length,
        not_mapped_controls_excluding_failed: rows.filter((row) => String(row.mapping_status || '').toLowerCase() === 'not_mapped').length,
        failed_controls: rows.filter((row) => String(row.mapping_status || '').toLowerCase() === 'failed').length,
        orphan_reference_controls: rows.filter((row) => !!row.orphan_reference).length
      };
      setControlDescriptionMappings(rows);
      setControlDescriptionSummary(summary);
      return data;
    } catch (err) {
      return null;
    } finally {
      if (!silent) setControlDescriptionLoading(false);
    }
  };

  const refreshControlSentenceMappingV3Live = async (documentId) => {
    if (!documentId) return;
    if (intakeV3MappingRefreshInFlightRef.current) {
      intakeV3MappingRefreshQueuedRef.current = true;
      return;
    }
    intakeV3MappingRefreshInFlightRef.current = true;
    try {
      await loadControlSentenceMappingV3(documentId, true);
    } finally {
      intakeV3MappingRefreshInFlightRef.current = false;
      if (intakeV3MappingRefreshQueuedRef.current) {
        intakeV3MappingRefreshQueuedRef.current = false;
        refreshControlSentenceMappingV3Live(documentId);
      }
    }
  };

  const fetchIntakeV3RunStatus = async (documentId) => {
    if (!documentId) return null;
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId
      });
      const response = await fetch(`${API_BASE}/documents/${documentId}/intake-v3/run-status?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!response.ok) return null;
      const data = await response.json();
      return data?.run || null;
    } catch (err) {
      return null;
    }
  };

  const fetchIntakeV4RunStatus = async (documentId, silent = false) => {
    if (!documentId) return null;
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId
      });
      const response = await fetch(`${API_BASE}/documents/${documentId}/intake-v4/run-status?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!response.ok) {
        if (response.status === 404) {
          setIntakeV4Available(false);
          return null;
        }
        if (!silent) {
          const err = await response.json().catch(() => ({}));
          setIntakeV4Error(err.detail || 'Failed to load intake v4 run status.');
        }
        return null;
      }
      setIntakeV4Available(true);
      const data = await response.json();
      const run = data?.run || null;
      setIntakeV4Run(run);
      setIntakeV4RunId(run?.run_execution_id || null);
      return run;
    } catch (err) {
      if (!silent) {
        setIntakeV4Error(`Failed to load intake v4 run status: ${err.message}`);
      }
      return null;
    }
  };

  const loadIntakeV4Escalations = async (documentId, runExecutionId = null, silent = false) => {
    if (!documentId) return null;
    const runIdToUse = runExecutionId || intakeV4RunId;
    if (!runIdToUse) {
      setIntakeV4Escalations([]);
      return [];
    }
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId,
        run_execution_id: runIdToUse
      });
      const response = await fetch(`${API_BASE}/documents/${documentId}/intake-v4/escalations?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!response.ok) {
        if (response.status === 404) {
          setIntakeV4Available(false);
          return [];
        }
        if (!silent) {
          const err = await response.json().catch(() => ({}));
          setIntakeV4Error(err.detail || 'Failed to load intake v4 escalations.');
        }
        return [];
      }
      const data = await response.json();
      const items = data?.items || [];
      setIntakeV4Escalations(items);
      return items;
    } catch (err) {
      if (!silent) {
        setIntakeV4Error(`Failed to load intake v4 escalations: ${err.message}`);
      }
      return [];
    }
  };

  const refreshIntakeV4Views = async (documentId, runExecutionId = null) => {
    if (!documentId) return;
    const runIdToUse = runExecutionId || intakeV4RunId;
    await Promise.all([
      loadExtractedControls(documentId),
      refreshSectionExtractionViews(documentId, true),
      loadPersonnelEntries(documentId),
      runIdToUse ? loadIntakeV4Escalations(documentId, runIdToUse, true) : Promise.resolve([]),
      runIdToUse ? loadIntakeV4MappingRows(documentId, runIdToUse, true) : Promise.resolve(null),
    ]);
  };

  const startIntakeV4StatusPolling = (documentId) => {
    stopIntakeV4StatusPolling();
    intakeV4StatusPollingRef.current = setInterval(async () => {
      const run = await fetchIntakeV4RunStatus(documentId, true);
      if (!run) return;
      const status = String(run.status || '').toLowerCase();
      setIntakeV4Run(run);
      if (status === 'running' || status === 'in_progress') {
        setIntakeV4Running(true);
        setIntakeV4Message('Intake v4 is running...');
        return;
      }
      stopIntakeV4StatusPolling();
      setIntakeV4Running(false);
      if (status === 'pass' || status === 'warning') {
        setIntakeV4Message(`Intake v4 completed with status: ${status}.`);
      } else if (status === 'fail') {
        setIntakeV4Error(run.error || 'Intake v4 failed.');
      }
      if (run.run_execution_id) {
        refreshIntakeV4Views(documentId, run.run_execution_id);
      }
    }, 5000);
  };

  const startIntakeV4EventStream = (documentId, runExecutionId) => {
    stopIntakeV4EventSource();
    stopIntakeV4StatusPolling();
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId,
        run_execution_id: runExecutionId
      });
      const source = new EventSource(`${API_BASE}/documents/${documentId}/intake-v4/events?${params.toString()}`);
      intakeV4EventSourceRef.current = source;

      source.onopen = () => {
        setIntakeV4Message('Connected to intake v4 live updates...');
      };

      source.addEventListener('run_started', () => {
        resetIntakeV4BatchProgress();
        setIntakeV4Running(true);
        setIntakeV4Message('Intake v4 started.');
      });
      source.addEventListener('stage_started', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          const stage = payload?.stage || 'stage';
          setIntakeV4Message(`Intake v4 ${stage} started...`);
        } catch (err) {
          setIntakeV4Message('Intake v4 stage started...');
        }
      });
      source.addEventListener('stage_aggregated', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          const stage = payload?.stage || 'stage';
          const status = payload?.status || 'completed';
          setIntakeV4Message(`Intake v4 ${stage} completed with status: ${status}.`);
          if (String(stage).toLowerCase() === 'extraction') {
            refreshSectionExtractionViews(documentId, true);
            loadExtractedControls(documentId);
          }
        } catch (err) {
          setIntakeV4Message('Intake v4 stage aggregated.');
        }
        fetchIntakeV4RunStatus(documentId, true);
        loadIntakeV4MappingRows(documentId, runExecutionId, true);
      });
      source.addEventListener('item_completed', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          handleIntakeV4ItemCompleted(documentId, runExecutionId, payload);
        } catch (err) {
          fetchIntakeV4RunStatus(documentId, true);
        }
      });
      source.addEventListener('batch_started', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          applyIntakeV4BatchEvent('batch_started', payload);
        } catch (err) {
          // no-op
        }
      });
      source.addEventListener('batch_completed', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          applyIntakeV4BatchEvent('batch_completed', payload);
        } catch (err) {
          // no-op
        }
      });
      source.addEventListener('batch_failed', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          applyIntakeV4BatchEvent('batch_failed', payload);
        } catch (err) {
          // no-op
        }
      });
      source.addEventListener('batch_retry_scheduled', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          applyIntakeV4BatchEvent('batch_retry_scheduled', payload);
        } catch (err) {
          // no-op
        }
      });
      source.addEventListener('run_warning', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          setIntakeV4Message(payload?.summary || 'Intake v4 completed with warnings.');
        } catch (err) {
          setIntakeV4Message('Intake v4 completed with warnings.');
        }
      });
      source.addEventListener('run_completed', () => {
        setIntakeV4Running(false);
        fetchIntakeV4RunStatus(documentId, true).then((run) => {
          if (run?.run_execution_id) {
            refreshIntakeV4Views(documentId, run.run_execution_id);
          }
        });
        setIntakeV4Message('Intake v4 completed.');
        stopIntakeV4EventSource();
      });
      source.addEventListener('run_failed', (event) => {
        let message = 'Intake v4 failed.';
        try {
          const payload = JSON.parse(event.data || '{}');
          message = payload?.summary || message;
        } catch (err) {
          // no-op
        }
        setIntakeV4Running(false);
        setIntakeV4Error(message);
        fetchIntakeV4RunStatus(documentId, true).then((run) => {
          if (run?.run_execution_id) {
            refreshIntakeV4Views(documentId, run.run_execution_id);
          }
        });
        stopIntakeV4EventSource();
      });
      source.addEventListener('stream_closed', () => {
        stopIntakeV4EventSource();
      });
      source.onerror = () => {
        stopIntakeV4EventSource();
        startIntakeV4StatusPolling(documentId);
      };
    } catch (err) {
      startIntakeV4StatusPolling(documentId);
    }
  };

  const startIntakeV3StatusPolling = (documentId) => {
    stopIntakeV3StatusPolling();
    intakeV3StatusPollingRef.current = setInterval(async () => {
      const run = await fetchIntakeV3RunStatus(documentId);
      if (!run) return;
      setControlDescriptionRun(run);
      const status = String(run.status || '').toLowerCase();
      if (status === 'completed') {
        stopIntakeV3StatusPolling();
        setControlDescriptionRunning(false);
        setControlDescriptionMessage('Intake v3 completed.');
        refreshSectionExtractionViews(documentId, true);
        loadPersonnelEntries(documentId);
        loadControlSentenceMappingV3(documentId, true);
      } else if (status === 'failed') {
        stopIntakeV3StatusPolling();
        setControlDescriptionRunning(false);
        setControlDescriptionError(run.error || 'Intake v3 failed.');
      }
    }, 5000);
  };

  const startIntakeV3EventStream = (documentId, runId) => {
    stopIntakeV3EventSource();
    stopIntakeV3StatusPolling();
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId,
        run_id: runId
      });
      const source = new EventSource(`${API_BASE}/documents/${documentId}/intake-v3/events?${params.toString()}`);
      intakeV3EventSourceRef.current = source;

      source.onopen = () => {
        setControlDescriptionMessage('Connected to intake v3 live updates...');
      };

      source.addEventListener('run_started', () => {
        setControlDescriptionRunning(true);
        setControlDescriptionMessage('Intake v3 started.');
      });
      source.addEventListener('section_i_completed', () => {
        setSectionExtractionMessage('Section I extraction completed.');
        refreshSectionExtractionViews(documentId, true);
      });
      source.addEventListener('section_ii_completed', () => {
        setSectionExtractionMessage('Section II extraction completed.');
        refreshSectionExtractionViews(documentId, true);
      });
      source.addEventListener('section_iii_completed', () => {
        setSectionExtractionMessage('Section III extraction completed.');
        refreshSectionExtractionViews(documentId, true);
        loadPersonnelEntries(documentId);
      });
      source.addEventListener('sentence_catalog_ready', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          const sentenceCount = payload?.sentence_count ?? 0;
          setControlDescriptionMessage(`Section III sentence catalog ready (${sentenceCount} sentences).`);
        } catch (err) {
          setControlDescriptionMessage('Section III sentence catalog ready.');
        }
      });
      source.addEventListener('mapping_item_completed', (event) => {
        try {
          const payload = JSON.parse(event.data || '{}');
          const done = payload?.progress?.done ?? 0;
          const total = payload?.progress?.total ?? 0;
          setControlDescriptionMessage(`Mapping in progress... ${done}/${total}`);
          refreshControlSentenceMappingV3Live(documentId);
        } catch (err) {
          setControlDescriptionMessage('Mapping in progress...');
        }
      });
      source.addEventListener('mapping_completed', () => {
        setControlDescriptionMessage('Mapping completed.');
        setControlDescriptionRunning(false);
        setControlDescriptionRun((prev) => ({ ...(prev || {}), run_id: runId, status: 'completed' }));
        loadControlSentenceMappingV3(documentId, true);
      });
      source.addEventListener('run_completed', () => {
        setControlDescriptionRunning(false);
        setControlDescriptionMessage('Intake v3 completed.');
        setControlDescriptionRun((prev) => ({ ...(prev || {}), run_id: runId, status: 'completed' }));
        refreshSectionExtractionViews(documentId, true);
        loadPersonnelEntries(documentId);
        loadControlSentenceMappingV3(documentId, true);
        stopIntakeV3EventSource();
      });
      source.addEventListener('run_failed', (event) => {
        let message = 'Intake v3 failed.';
        try {
          const payload = JSON.parse(event.data || '{}');
          if (payload?.error) message = payload.error;
        } catch (err) {
          // no-op
        }
        setControlDescriptionError(message);
        setControlDescriptionRunning(false);
        setControlDescriptionRun((prev) => ({ ...(prev || {}), run_id: runId, status: 'failed', error: message }));
        stopIntakeV3EventSource();
      });
      source.addEventListener('stream_closed', () => {
        stopIntakeV3EventSource();
      });
      source.onerror = () => {
        stopIntakeV3EventSource();
        startIntakeV3StatusPolling(documentId);
      };
    } catch (err) {
      startIntakeV3StatusPolling(documentId);
    }
  };

  const startControlDescriptionPolling = (documentId) => {
    stopControlDescriptionPolling();
    controlDescriptionPollingRef.current = setInterval(async () => {
      const data = await loadControlDescriptionMapping(documentId, true);
      const status = (data?.run?.status || '').toLowerCase();
      const mappingStateStatus = String(data?.mapping_state?.status || '').toLowerCase();
      const running = status === 'running' || mappingStateStatus === 'running';
      if (!running) {
        stopControlDescriptionPolling();
        if (status === 'completed' || mappingStateStatus === 'current') {
          setControlDescriptionMessage('Control-to-description mapping completed.');
        } else if (status === 'failed' || mappingStateStatus === 'failed') {
          setControlDescriptionError(data?.mapping_state?.reason_detail || data?.run?.error || 'Control-to-description mapping failed.');
        } else if (mappingStateStatus === 'blocked') {
          setControlDescriptionMessage(data?.mapping_state?.reason_detail || 'Mapping is blocked.');
        }
      }
    }, 10000);
  };

  const loadPersonnelEntries = async (documentId) => {
    if (!documentId) return;
    setPersonnelLoading(true);
    try {
      const params = new URLSearchParams({
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId
      });
      const response = await fetch(`${API_BASE}/documents/${documentId}/personnel?${params.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      if (!response.ok) {
        throw new Error('Failed to load personnel');
      }
      const data = await response.json();
      setPersonnelEntries(data.entries || []);
      setPersonnelSummary(data.summary || null);
    } catch (err) {
      console.error('Error loading personnel:', err);
      setPersonnelError('Failed to load personnel entries.');
    } finally {
      setPersonnelLoading(false);
    }
  };

  const resetPersonnelForm = () => {
    setNewPersonnel({
      full_name: '',
      position_title: '',
      page_start: '',
      page_end: '',
      org_unit: ''
    });
  };

  const getPersonnelStatusMessage = () => {
    const status = String(personnelSummary?.personnel_status || '').trim().toLowerCase();
    if (status === 'success_empty') {
      return 'No personnel detected in Section III.';
    }
    if (status === 'success_dropped_invalid') {
      return 'Personnel extraction returned incomplete rows and none were accepted.';
    }
    if (status === 'failed') {
      return personnelSummary?.personnel_error || 'Personnel extraction failed.';
    }
    return 'No personnel entries yet. Run Section I–III extraction or add manually.';
  };

  const shouldShowPersonnelStatusNotice = () => {
    const status = String(personnelSummary?.personnel_status || '').trim().toLowerCase();
    return Boolean(status) && status !== 'success_with_entries';
  };

  const handleAddPersonnel = async () => {
    if (!selectedDocument) return;
    if (!newPersonnel.full_name.trim() || !newPersonnel.position_title.trim()) {
      setPersonnelError('Full name and position are required.');
      return;
    }
    setPersonnelError('');
    try {
      const payload = {
        auditor_id: userInfo?.user_id || 'default',
        engagement_id: engagementId,
        entries: [{
          full_name: newPersonnel.full_name.trim(),
          position_title: newPersonnel.position_title.trim(),
          page_start: newPersonnel.page_start ? Number(newPersonnel.page_start) : null,
          page_end: newPersonnel.page_end ? Number(newPersonnel.page_end) : null,
          org_unit: newPersonnel.org_unit.trim() || null
        }]
      };
      const response = await fetch(`${API_BASE}/documents/${selectedDocument.id}/personnel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error('Failed to create personnel');
      }
      resetPersonnelForm();
      loadPersonnelEntries(selectedDocument.id);
    } catch (err) {
      console.error('Error adding personnel:', err);
      setPersonnelError('Failed to add personnel entry.');
    }
  };

  const startEditPersonnel = (entry) => {
    setEditingPersonnelId(entry.id);
    setEditingPersonnel({
      full_name: entry.full_name || '',
      position_title: entry.position_title || '',
      page_start: entry.page_start ?? '',
      page_end: entry.page_end ?? '',
      org_unit: entry.org_unit || ''
    });
  };

  const cancelEditPersonnel = () => {
    setEditingPersonnelId(null);
    setEditingPersonnel({
      full_name: '',
      position_title: '',
      page_start: '',
      page_end: '',
      org_unit: ''
    });
  };

  const handleSavePersonnelEdit = async (personnelId) => {
    if (!personnelId) return;
    if (!editingPersonnel.full_name.trim() || !editingPersonnel.position_title.trim()) {
      setPersonnelError('Full name and position are required.');
      return;
    }
    setPersonnelError('');
    try {
      const payload = {
        full_name: editingPersonnel.full_name.trim(),
        position_title: editingPersonnel.position_title.trim(),
        page_start: editingPersonnel.page_start ? Number(editingPersonnel.page_start) : null,
        page_end: editingPersonnel.page_end ? Number(editingPersonnel.page_end) : null,
        org_unit: editingPersonnel.org_unit.trim() || null,
        auditor_id: userInfo?.user_id || 'default'
      };
      const response = await fetch(`${API_BASE}/personnel/${personnelId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        throw new Error('Failed to update personnel');
      }
      cancelEditPersonnel();
      loadPersonnelEntries(selectedDocument.id);
    } catch (err) {
      console.error('Error updating personnel:', err);
      setPersonnelError('Failed to update personnel entry.');
    }
  };

  const handleDeletePersonnel = async (personnelId) => {
    if (!personnelId) return;
    setPersonnelError('');
    try {
      const response = await fetch(`${API_BASE}/personnel/${personnelId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        }
      });
      if (!response.ok) {
        throw new Error('Failed to delete personnel');
      }
      loadPersonnelEntries(selectedDocument.id);
    } catch (err) {
      console.error('Error deleting personnel:', err);
      setPersonnelError('Failed to delete personnel entry.');
    }
  };

  const groupedControls = useMemo(() => {
    const groups = new Map();
    (extractedControls || []).forEach((ctrl) => {
      const status = String(ctrl.extraction_status || 'valid').trim().toLowerCase();
      const rawKey = (ctrl.tsc_control_id || '').trim();
      const key = status === 'needs_review'
        ? 'NEEDS_REVIEW'
        : status === 'rejected'
          ? 'REJECTED'
          : rawKey || 'UNMAPPED';
      const name = (ctrl.tsc_control_name || '').trim();
      if (!groups.has(key)) {
        const label = key === 'NEEDS_REVIEW'
          ? 'Needs review'
          : key === 'REJECTED'
            ? 'Rejected extraction rows'
            : key === 'UNMAPPED'
          ? 'Unmapped controls'
          : `${key}${name ? ` — ${name}` : ''}`;
        groups.set(key, { key, label, items: [] });
      }
      groups.get(key).items.push(ctrl);
    });
    const grouped = Array.from(groups.values());
    grouped.forEach((group) => {
      group.items.sort((a, b) =>
        ((Number(a.source_page) || Number.MAX_SAFE_INTEGER) - (Number(b.source_page) || Number.MAX_SAFE_INTEGER)) ||
        String(a.control_id || '').localeCompare(String(b.control_id || ''), undefined, {
          numeric: true,
          sensitivity: 'base'
        })
      );
      group.minPage = group.items.reduce((min, item) => {
        const page = Number(item.source_page) || Number.MAX_SAFE_INTEGER;
        return page < min ? page : min;
      }, Number.MAX_SAFE_INTEGER);
    });
    const groupPriority = (key) => {
      if (key === 'NEEDS_REVIEW') return 0;
      if (key === 'REJECTED') return 1;
      if (key === 'UNMAPPED') return 2;
      return 3;
    };
    return grouped.sort((a, b) =>
      (groupPriority(a.key) - groupPriority(b.key)) ||
      (a.minPage - b.minPage) ||
      String(a.key).localeCompare(String(b.key), undefined, { numeric: true, sensitivity: 'base' })
    );
  }, [extractedControls]);

  const controlsTotal = useMemo(
    () => groupedControls.reduce((acc, group) => acc + group.items.length, 0),
    [groupedControls]
  );

  const controlsNeedsReviewCount = useMemo(
    () => (extractedControls || []).filter((ctrl) =>
      String(ctrl?.extraction_status || '').trim().toLowerCase() === 'needs_review'
    ).length,
    [extractedControls]
  );

  const controlsRejectedCount = useMemo(
    () => (extractedControls || []).filter((ctrl) =>
      String(ctrl?.extraction_status || '').trim().toLowerCase() === 'rejected'
    ).length,
    [extractedControls]
  );

  const notDetectedCount = useMemo(
    () => extractedControls.filter((ctrl) =>
      (ctrl.tsc_control_id || '').trim().toLowerCase() === 'not detected'
    ).length,
    [extractedControls]
  );

  const canRerunSectionIV = useMemo(
    () => Boolean(selectedDocument?.id) && !controlsStatus.in_progress,
    [controlsStatus, selectedDocument?.id]
  );

  const mappingStatus = String(controlDescriptionRun?.status || '').toLowerCase();
  const mappingDependencyStatus = String(controlDescriptionMappingState?.status || '').toLowerCase();
  const mappingDependencyStatusLabel = useMemo(() => {
    const raw = String(controlDescriptionMappingState?.status || '').toLowerCase();
    if (!raw) return '';
    if (raw === 'current') return 'Complete';
    if (raw === 'partial') return 'Partial';
    if (raw === 'blocked') return 'Blocked';
    if (raw === 'failed') return 'Failed';
    return raw.replace(/_/g, ' ');
  }, [controlDescriptionMappingState?.status]);
  const mappingFailed = useMemo(
    () => mappingStatus === 'failed'
      || mappingDependencyStatus === 'failed'
      || Boolean(String(controlDescriptionError || '').trim())
      || String(intakeV4Summary.mapping_status || '').toLowerCase() === 'fail',
    [controlDescriptionError, intakeV4Summary, mappingDependencyStatus, mappingStatus]
  );

  const mappingNoData = useMemo(
    () => !controlDescriptionRunning
      && controlDescriptionMappings.length === 0
      && Boolean(
        mappingStatus
        || String(intakeV4Summary.mapping_status || '').trim()
      ),
    [controlDescriptionMappings.length, controlDescriptionRunning, intakeV4Summary, mappingStatus]
  );

  const canRerunMapping = useMemo(
    () => !controlDescriptionRunning && (mappingFailed || mappingNoData),
    [controlDescriptionRunning, mappingFailed, mappingNoData]
  );

  const toggleControlGroup = (key) => {
    setExpandedControlGroups((prev) => ({
      ...prev,
      [key]: prev[key] === false
    }));
  };

  const handleEditControl = (ctrl) => {
    setEditingControl({
      id: ctrl.id,
      control_id: ctrl.control_id || '',
      description: ctrl.description || '',
      tsc_control_id: ctrl.tsc_control_id || '',
      tsc_control_name: ctrl.tsc_control_name || '',
      test_result: ctrl.test_result || '',
      source_page: ctrl.source_page ?? '',
      reason_code: '',
      original_control_id: ctrl.control_id || '',
      original_description: ctrl.description || '',
      original_tsc_control_id: ctrl.tsc_control_id || '',
      original_tsc_control_name: ctrl.tsc_control_name || '',
      original_test_result: ctrl.test_result || '',
      original_source_page: ctrl.source_page ?? ''
    });
  };

  const handleEditMapping = async (row) => {
    if (!selectedDocument) return;
    let sentences = sectionSentenceCatalog;
    if (!sentences.length) {
      sentences = await loadSectionIIISentences(selectedDocument.id);
    }
    const selectedSentenceIds = row.selected_sentence_ids_final
      || row.selected_sentence_ids_ai
      || (row.primary_sentence_id_final || row.primary_sentence_id_ai ? [row.primary_sentence_id_final || row.primary_sentence_id_ai] : []);
    const primarySentenceId = row.primary_sentence_id_final || row.primary_sentence_id_ai || '';
    const mappedText = row.mapped_paragraph_text_final || row.mapped_text_final || row.mapped_text_ai || '';
    const mappingConfidence = row.mapping_confidence_final ?? row.mapping_confidence_ai ?? '';
    const accepted = Boolean(row.is_accepted);
    setEditingMapping({
      id: row.id,
      lookup_id: row.extracted_control_row_id || row.id || row.internal_control_id_final || row.internal_control_id_ai || '',
      control_id: row.internal_control_id_final || row.internal_control_id_ai || '',
      internal_control_statement: row.internal_control_statement_final || row.internal_control_statement_ai || '',
      selected_sentence_ids: Array.isArray(selectedSentenceIds) ? selectedSentenceIds.join(', ') : '',
      primary_sentence_id: primarySentenceId,
      mapped_text: mappedText,
      mapping_confidence: mappingConfidence === null || mappingConfidence === undefined ? '' : String(mappingConfidence),
      is_accepted: accepted,
      reason_code: '',
      reason_description: '',
      original_selected_sentence_ids: Array.isArray(selectedSentenceIds) ? selectedSentenceIds.join(', ') : '',
      original_primary_sentence_id: primarySentenceId,
      original_mapped_text: mappedText,
      original_mapping_confidence: mappingConfidence === null || mappingConfidence === undefined ? '' : String(mappingConfidence),
      original_is_accepted: accepted,
      available_sentence_ids: (sentences || []).map((entry) => String(entry.sentence_id || '')).filter(Boolean),
    });
  };

  const mappingEditHasChanges = useMemo(() => {
    if (!editingMapping) return false;
    return (
      (editingMapping.selected_sentence_ids || '') !== (editingMapping.original_selected_sentence_ids || '') ||
      (editingMapping.primary_sentence_id || '') !== (editingMapping.original_primary_sentence_id || '') ||
      (editingMapping.mapped_text || '') !== (editingMapping.original_mapped_text || '') ||
      String(editingMapping.mapping_confidence || '') !== String(editingMapping.original_mapping_confidence || '') ||
      Boolean(editingMapping.is_accepted) !== Boolean(editingMapping.original_is_accepted)
    );
  }, [editingMapping]);

  const handleSubmitMappingEdit = async (e) => {
    e.preventDefault();
    if (!editingMapping || !selectedDocument) return;
    if (mappingEditHasChanges && !String(editingMapping.reason_code || '').trim()) {
      setError('Reason code is required for mapping corrections.');
      return;
    }
    if (mappingEditHasChanges && !String(editingMapping.reason_description || '').trim()) {
      setError('Reason description is required for mapping corrections.');
      return;
    }
    try {
      const selectedSentenceIds = (editingMapping.selected_sentence_ids || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      const payload = {
        auditor_id: userInfo?.user_id || 'default',
        reviewer_role: userInfo?.role || null,
        engagement_id: engagementId,
        reason_code: editingMapping.reason_code || null,
        reason_description: editingMapping.reason_description || null,
        selected_sentence_ids_final: selectedSentenceIds,
        primary_sentence_id_final: editingMapping.primary_sentence_id || null,
        mapped_text_final: editingMapping.mapped_text || null,
        mapping_confidence_final: editingMapping.mapping_confidence === '' ? null : Number(editingMapping.mapping_confidence),
        is_accepted: Boolean(editingMapping.is_accepted),
      };
      const mappingLookupId = editingMapping.lookup_id || editingMapping.control_id;
      const response = await fetch(`${API_BASE}/documents/${selectedDocument.id}/control-sentence-mapping/${encodeURIComponent(mappingLookupId)}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.detail || 'Failed to update mapping');
      }
      setEditingMapping(null);
      loadControlSentenceMappingV3(selectedDocument.id, true);
    } catch (err) {
      setError(`Failed to update mapping: ${err.message}`);
    }
  };

  const controlEditHasChanges = useMemo(() => {
    if (!editingControl) return false;
    return (
      (editingControl.control_id || '') !== (editingControl.original_control_id || '') ||
      (editingControl.description || '') !== (editingControl.original_description || '') ||
      (editingControl.tsc_control_id || '') !== (editingControl.original_tsc_control_id || '') ||
      (editingControl.tsc_control_name || '') !== (editingControl.original_tsc_control_name || '') ||
      (editingControl.test_result || '') !== (editingControl.original_test_result || '') ||
      String(editingControl.source_page ?? '') !== String(editingControl.original_source_page ?? '')
    );
  }, [editingControl]);

  const handleSubmitControlEdit = async (e) => {
    e.preventDefault();
    if (!editingControl || !selectedDocument) return;
    if (controlEditHasChanges && !String(editingControl.reason_code || '').trim()) {
      setError('Reason code is required for control corrections.');
      return;
    }
    try {
      const payload = {
        auditor_id: userInfo?.user_id || 'default',
        reviewer_role: userInfo?.role || null,
        reason_code: editingControl.reason_code || null,
        control_id: editingControl.control_id || null,
        description: editingControl.description || null,
        tsc_control_id: editingControl.tsc_control_id || null,
        tsc_control_name: editingControl.tsc_control_name || null,
        test_result: editingControl.test_result || null,
        source_page: editingControl.source_page === '' ? null : Number(editingControl.source_page)
      };
      const resp = await fetch(`${API_BASE}/documents/${selectedDocument.id}/controls/${editingControl.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      if (resp.ok) {
        const editedControlRowId = editingControl.id;
        const editedDocumentId = selectedDocument.id;
        setEditingControl(null);
        loadExtractedControls(editedDocumentId);
        loadControlSentenceMappingV3(editedDocumentId, true);
        setControlDescriptionMessage('Mapping refresh started for edited control...');
        waitForEditedControlMapping(editedDocumentId, editedControlRowId);
      } else {
        const err = await resp.json();
        setError(err.detail || 'Failed to update control');
      }
    } catch (e) {
      setError('Failed to update control: ' + e.message);
    }
  };

  const renderControlCell = (value, extraClass = '') => {
    const display = value === null || value === undefined || value === '' ? '—' : value;
    const isNotDetected = display === 'Not detected';
    const classes = ['cell', extraClass, isNotDetected ? 'not-detected' : ''].filter(Boolean).join(' ');
    return <span className={classes}>{display}</span>;
  };

  const formatControlConfidence = (ctrl) => {
    const num = Number(ctrl?.row_confidence);
    if (!Number.isFinite(num)) return '—';
    return num.toFixed(2);
  };

  const formatControlNotes = (ctrl) => {
    const notes = [];
    const reviewReason = String(ctrl?.review_reason || '').trim();
    const qualityFlags = Array.isArray(ctrl?.quality_flags_json) ? ctrl.quality_flags_json.filter(Boolean) : [];
    const failureLabels = Array.isArray(ctrl?.failure_labels) ? ctrl.failure_labels.filter(Boolean) : [];
    const unresolvedFields = Array.isArray(ctrl?.unresolved_fields) ? ctrl.unresolved_fields.filter(Boolean) : [];
    if (reviewReason) {
      notes.push(`review:${reviewReason}`);
    }
    if (qualityFlags.length > 0) {
      notes.push(`quality:${qualityFlags.join(',')}`);
    }
    notes.push(...failureLabels);
    if (unresolvedFields.length > 0) {
      notes.push(`unresolved:${unresolvedFields.join(',')}`);
    }
    return notes.length > 0 ? notes.join(' | ') : 'ok';
  };

  const getControlExtractionStatus = (ctrl) => (
    String(ctrl?.extraction_status || 'valid').trim().toLowerCase() || 'valid'
  );

  const renderControlExtractionStatus = (ctrl) => {
    const status = getControlExtractionStatus(ctrl);
    const label = status.replace(/_/g, ' ');
    return <span className={`status-pill extraction-${status}`}>{label}</span>;
  };

  const formatMappingConfidence = (row) => {
    const raw = row?.mapping_confidence_final
      ?? row?.mapping_confidence_ai
      ?? row?.item_state_confidence
      ?? row?.relevance_score_final
      ?? row?.relevance_score_ai;
    const num = Number(raw);
    if (!Number.isFinite(num)) return '—';
    return num.toFixed(3);
  };

  const formatMappingNotes = (row) => {
    const tags = Array.isArray(row?.note_tags) ? row.note_tags : [];
    if (tags.length === 0) {
      if (String(row?.mapping_status || '').toLowerCase() === 'failed') return 'failed';
      return row?.is_mapped ? 'mapped' : 'not mapped';
    }
    const labels = tags.map((tag) => {
      if (tag === 'orphan_reference') return 'orphan reference';
      if (tag === 'orphan_primary_sentence') return 'orphan primary sentence';
      if (tag === 'missing_selected_sentences') return 'missing selected sentences';
      if (tag === 'not_mapped') return 'not mapped';
      return String(tag).replace(/_/g, ' ');
    });
    return labels.join(', ');
  };

  const getMappingNoteClass = (row) => {
    const tags = new Set(Array.isArray(row?.note_tags) ? row.note_tags : []);
    if (tags.has('failed') || tags.has('orphan_reference')) {
      return 'match-warning';
    }
    if (tags.has('not_mapped')) {
      return 'mapping-note-neutral';
    }
    return '';
  };

  const isSameValue = (left, right) => {
    if (!left || !right) return false;
    if (left === 'Not detected' || right === 'Not detected') return false;
    return String(left).trim().toLowerCase() === String(right).trim().toLowerCase();
  };

  const toggleControlSelection = (controlRowId) => {
    setSelectedControlIds((prev) => {
      const next = new Set(prev);
      if (next.has(controlRowId)) {
        next.delete(controlRowId);
      } else {
        next.add(controlRowId);
      }
      return next;
    });
  };

  const handleRerunSelectedControls = async () => {
    if (!selectedDocument || selectedControlIds.size === 0) return;
    const confirmed = window.confirm(
      `Re-analyze ${selectedControlIds.size} control(s). Confirm control ID and page are accurate before proceeding.`
    );
    if (!confirmed) return;
    setRerunInProgress(true);
    setRerunMessage('');
    try {
      for (const controlRowId of selectedControlIds) {
        const payload = {
          auditor_id: userInfo?.user_id || 'default',
          engagement_id: engagementId
        };
        const resp = await fetch(`${API_BASE}/documents/${selectedDocument.id}/controls/${controlRowId}/rerun`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        });
        if (!resp.ok) {
          const err = await resp.json();
          setError(err.detail || 'Failed to rerun control');
        } else {
          setExtractedControls((prev) => prev.map((ctrl) =>
            ctrl.id === controlRowId ? { ...ctrl, rerun_status: 'running' } : ctrl
          ));
        }
      }
      setRerunMessage('Re-analysis queued. Use Show Controls to refresh results.');
      setSelectedControlIds(new Set());
    } catch (e) {
      setError('Failed to rerun controls: ' + e.message);
    } finally {
      setRerunInProgress(false);
    }
  };

  const handleRunSectionExtraction = async () => {
    if (!selectedDocument) return;
    if (!selectedDocument.section_headings || selectedDocument.section_headings.length === 0) {
      setError('No section ranges found. Add or confirm sections before running AI extraction.');
      return;
    }
    setSectionExtractionRunning(true);
    setSectionExtractionMessage('');
    try {
      const formData = new FormData();
      formData.append('auditor_id', userInfo?.user_id || 'default');
      formData.append('engagement_id', engagementId);
      const resp = await fetch(`${API_BASE}/documents/${selectedDocument.id}/section-extractions/ai`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData
      });
      if (resp.ok) {
        setSectionExtractionMessage('Section I–III extraction in progress. This may take a few minutes...');
        setControlDescriptionMappings([]);
        setControlDescriptionMappingState({
          status: 'stale',
          reason_code: 'SECTION_III_REEXTRACTION_STARTED',
          reason_detail: 'Section III extraction is running. Mapping will refresh when prerequisites are ready.'
        });
        setControlDescriptionMessage('Mapping pending while Section III extraction runs.');
        startSectionStatusPolling(selectedDocument.id);
      } else {
        const err = await resp.json();
        setError(err.detail || 'Failed to start section extraction');
      }
    } catch (e) {
      setError('Failed to start section extraction: ' + e.message);
    } finally {
      setSectionExtractionRunning(false);
    }
  };

  const handleRerunSectionExtraction = async (sectionCode) => {
    if (!selectedDocument) return;
    const normalizedSectionCode = String(sectionCode || '').trim().toUpperCase();
    const canonicalSection = canonicalSectionHeadingByCode.get(normalizedSectionCode);
    const sectionId = String(canonicalSection?.id || '').trim();
    if (!sectionId) {
      setError('Section rerun failed: section is missing an internal ID.');
      return;
    }

    const confirmed = window.confirm(
      `Re-extracting ${CANONICAL_SECTION_LABELS[normalizedSectionCode] || normalizedSectionCode} will replace the current extracted content for that section. Continue?`
    );
    if (!confirmed) {
      return;
    }

    setSectionExtractionRunning(true);
    setSectionExtractionMessage('');
    setError(null);
    try {
      const formData = new FormData();
      formData.append('auditor_id', userInfo?.user_id || 'default');
      formData.append('engagement_id', engagementId);
      formData.append('section_ids', sectionId);
      const resp = await fetch(`${API_BASE}/documents/${selectedDocument.id}/section-extractions/ai`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData
      });
      if (resp.ok) {
        setSectionExtractionMessage(`${CANONICAL_SECTION_LABELS[normalizedSectionCode] || normalizedSectionCode} re-extraction started. Existing section content will be replaced when the run completes.`);
        if (normalizedSectionCode === 'SECTION_III') {
          setControlDescriptionMappings([]);
          setControlDescriptionMappingState({
            status: 'stale',
            reason_code: 'SECTION_III_REEXTRACTION_STARTED',
            reason_detail: 'Section III extraction is running. Mapping will refresh when prerequisites are ready.'
          });
          setControlDescriptionMessage('Mapping pending while Section III extraction runs.');
        }
        await refreshSectionExtractionViews(selectedDocument.id, true);
        startSectionStatusPolling(selectedDocument.id);
      } else {
        const err = await resp.json();
        setError(err.detail || 'Failed to re-extract section');
      }
    } catch (e) {
      setError('Failed to re-extract section: ' + e.message);
    } finally {
      setSectionExtractionRunning(false);
    }
  };

  const handleRunControlDescriptionMapping = async () => {
    if (!selectedDocument) return;
    setControlDescriptionError('');
    setControlDescriptionMessage('');
    stopIntakeV3EventSource();
    stopIntakeV3StatusPolling();
    try {
      const formData = new FormData();
      formData.append('auditor_id', userInfo?.user_id || 'default');
      formData.append('engagement_id', engagementId);
      const v3Response = await fetch(`${API_BASE}/documents/${selectedDocument.id}/intake-v3/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData
      });
      if (v3Response.ok) {
        const data = await v3Response.json();
        const runId = data?.run_id || null;
        intakeV3MappingRefreshInFlightRef.current = false;
        intakeV3MappingRefreshQueuedRef.current = false;
        setIntakeV3Mode(true);
        setIntakeV3RunId(runId);
        setControlDescriptionRun({ run_id: runId, status: 'running' });
        setControlDescriptionMappings([]);
        setControlDescriptionSummary({
          processed_controls: 0,
          mapped_controls: 0,
          unmapped_controls: 0,
          failed_controls: 0,
          orphan_reference_controls: 0
        });
        setControlDescriptionRunning(true);
        setControlDescriptionMessage('Intake v3 started. Waiting for extraction and mapping updates...');
        if (runId) {
          startIntakeV3EventStream(selectedDocument.id, runId);
        } else {
          startIntakeV3StatusPolling(selectedDocument.id);
        }
        return;
      }
      let v3ErrorDetail = 'Failed to start intake v3 run.';
      try {
        const v3ErrJson = await v3Response.json();
        v3ErrorDetail = v3ErrJson.detail || v3ErrorDetail;
      } catch (err) {
        // keep default
      }
      if (v3Response.status !== 404) {
        setControlDescriptionError(v3ErrorDetail);
        return;
      }
      // Fallback to legacy mapping flow only when v3 endpoint is disabled/unavailable.
      const legacyResponse = await fetch(`${API_BASE}/documents/${selectedDocument.id}/control-description-mapping/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData
      });
      if (!legacyResponse.ok) {
        const err = await legacyResponse.json();
        setControlDescriptionError(err.detail || 'Failed to start control-description mapping');
        return;
      }
      setIntakeV3Mode(false);
      setIntakeV3RunId(null);
      setControlDescriptionRun(null);
      setControlDescriptionRunning(true);
      setControlDescriptionMessage('Control-to-description mapping is running...');
      startControlDescriptionPolling(selectedDocument.id);
    } catch (err) {
      setControlDescriptionError(`Failed to start control-description mapping: ${err.message}`);
    }
  };

  const handleRerunControlDescriptionMapping = async () => {
    if (!selectedDocument) return;
    const confirmed = window.confirm(
      'Re-extracting Internal Control to System Description Mapping will replace the current mapping results for this document. Continue?'
    );
    if (!confirmed) {
      return;
    }

    setControlDescriptionError('');
    setControlDescriptionMessage('');
    try {
      const formData = new FormData();
      formData.append('auditor_id', userInfo?.user_id || 'default');
      formData.append('engagement_id', engagementId);
      const response = await fetch(`${API_BASE}/documents/${selectedDocument.id}/control-description-mapping/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        setControlDescriptionError(err.detail || 'Failed to restart control-description mapping');
        return;
      }

      setIntakeV3Mode(false);
      setIntakeV3RunId(null);
      setControlDescriptionRun(null);
      setControlDescriptionRunning(true);
      setControlDescriptionMessage('Control-to-description mapping re-extraction is running...');
      startControlDescriptionPolling(selectedDocument.id);
    } catch (err) {
      setControlDescriptionError(`Failed to restart control-description mapping: ${err.message}`);
    }
  };

  const handleRunIntakeV4 = async () => {
    if (!selectedDocument) return;
    setIntakeV4Error('');
    setIntakeV4Message('');
    setIntakeV4Loading(true);
    stopIntakeV4EventSource();
    stopIntakeV4StatusPolling();
    try {
      const formData = new FormData();
      formData.append('auditor_id', userInfo?.user_id || 'default');
      formData.append('engagement_id', engagementId);

      const response = await fetch(`${API_BASE}/documents/${selectedDocument.id}/intake-v4/run`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData
      });

      if (!response.ok) {
        if (response.status === 404) {
          setIntakeV4Available(false);
          setIntakeV4Error('Intake v4 is disabled in this environment.');
          return;
        }
        const err = await response.json().catch(() => ({}));
        setIntakeV4Error(err.detail || 'Failed to start intake v4 run.');
        return;
      }

      setIntakeV4Available(true);
      const data = await response.json();
      const runExecutionId = data?.run_execution_id || null;
      setIntakeV4RunId(runExecutionId);
      setIntakeV4Run({ run_execution_id: runExecutionId, status: 'running' });
      setIntakeV4Escalations([]);
      resetIntakeV4BatchProgress();
      setIntakeV4Running(true);
      setIntakeV4Message('Intake v4 started. Waiting for live updates...');

      if (runExecutionId) {
        startIntakeV4EventStream(selectedDocument.id, runExecutionId);
      } else {
        startIntakeV4StatusPolling(selectedDocument.id);
      }
    } catch (err) {
      setIntakeV4Error(`Failed to start intake v4: ${err.message}`);
    } finally {
      setIntakeV4Loading(false);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadFiles(files);
    if (files.length > 0) {
      setShowUploadModal(true);
    }
  };

  const handleUpload = async () => {
    if (uploadFiles.length === 0) return;
    
    setUploading(true);
    setError(null);
    
    try {
      const formData = new FormData();
      formData.append('auditor_id', userInfo?.user_id || 'default');
      formData.append('engagement_id', engagementId);
      
      uploadFiles.forEach(file => {
        formData.append('files', file);
      });
      
      const response = await fetch(`${API_BASE}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData,
      });
      
      if (response.ok) {
        const data = await response.json();
        setShowUploadModal(false);
        setUploadFiles([]);
        // Reload documents after upload
        setTimeout(() => loadDocumentsCallback(), 2000); // Wait for processing
        alert(`Upload started! Job ID: ${data.job_id || 'processing'}`);
      } else {
        const err = await response.json();
        if (Array.isArray(err.detail)) {
          setError(err.detail.map(e => e.msg || JSON.stringify(e)).join(', '));
        } else if (typeof err.detail === 'object') {
          setError(JSON.stringify(err.detail));
        } else {
          setError(err.detail || 'Upload failed');
        }
      }
    } catch (err) {
      setError('Upload error: ' + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleValidate = async (documentId) => {
    setError(null);
    try {
      const formData = new FormData();
      formData.append('user_id', userInfo?.user_id || '1');
      if (validationNotes) {
        formData.append('notes', validationNotes);
      }
      
      const response = await fetch(`${API_BASE}/documents/${documentId}/validate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData,
      });
      
      if (response.ok) {
        loadDocumentsCallback();
        checkPlanningReadinessCallback();
        setValidationNotes('');
      } else {
        const err = await response.json();
        if (Array.isArray(err.detail)) {
          setError(err.detail.map(e => e.msg || JSON.stringify(e)).join(', '));
        } else if (typeof err.detail === 'object') {
          setError(JSON.stringify(err.detail));
        } else {
          setError(err.detail || 'Failed to validate document');
        }
      }
    } catch (err) {
      // Demo mode
      setDocuments(documents.map(d => 
        d.id === documentId 
          ? { ...d, validation_status: 'VALIDATED', validated_by: userInfo?.username, validated_at: new Date().toISOString() }
          : d
      ));
      checkPlanningReadinessCallback();
    }
  };

  const handleReject = async (documentId) => {
    setError(null);
    try {
      const confirmed = window.confirm(
        'Rejecting this document will permanently delete the uploaded file and all extracted data for it. Continue?'
      );
      if (!confirmed) {
        return;
      }
      // Per product requirement: "Reject & Re-Upload" deletes the document + file
      const response = await fetch(`${API_BASE}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
      });
      
      if (response.ok) {
        // Remove from UI immediately
        setSelectedDocument(null);
        setExtractedControls([]);
        loadDocumentsCallback();
        checkPlanningReadinessCallback();
      } else {
        const err = await response.json();
        // Handle Pydantic validation errors (array/object)
        if (Array.isArray(err.detail)) {
          setError(err.detail.map(e => e.msg || JSON.stringify(e)).join(', '));
        } else if (typeof err.detail === 'object') {
          setError(JSON.stringify(err.detail));
        } else {
          setError(err.detail || 'Failed to reject document');
        }
      }
    } catch (err) {
      setError('Failed to delete document: ' + err.message);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderDocumentCard = (doc) => {
    const docType = DOCUMENT_TYPES[doc.document_type] || DOCUMENT_TYPES['OTHER'];
    const status = VALIDATION_STATUS[doc.validation_status] || VALIDATION_STATUS['DRAFT'];
    
    return (
      <div 
        key={doc.id}
        className={`document-card ${selectedDocument?.id === doc.id ? 'selected' : ''}`}
        onClick={() => setSelectedDocument(doc)}
      >
        <div className="doc-icon">{docType.icon}</div>
        <div className="doc-info">
          <h4>{doc.filename}</h4>
          <div className="doc-meta">
            <span className="doc-type">{docType.label}</span>
            <span className="doc-size">{formatFileSize(doc.file_size)}</span>
          </div>
          {doc.file_checksum && (
            <div className="doc-checksum" title={doc.file_checksum}>
              🔒 {doc.file_checksum.substring(0, 20)}...
            </div>
          )}
          <div className="doc-metrics">
            <span>Pages: {doc.total_pages ?? '–'}</span>
            <span>Headings: {doc.total_sections ?? '–'}</span>
            <span>Tables: {doc.total_tables ?? '–'}</span>
            <span>Controls: {doc.controls_count ?? '–'}</span>
          </div>
        </div>
        <div className="doc-status">
          <span 
            className="status-badge" 
            style={{ background: status.color + '20', color: status.color }}
          >
            {status.icon} {status.label}
          </span>
          {doc.validated_by && (
            <span className="validated-by">
              by {doc.validated_by}
            </span>
          )}
        </div>
      </div>
    );
  };

  const handleReprocessControls = async () => {
    if (!selectedDocument) return;
    setReprocessMessage('');
    const status = await fetchControlsStatus(selectedDocument.id, true);
    if (status?.in_progress) {
      const startedAt = status.started_at ? new Date(status.started_at).toLocaleString() : 'now';
      setReprocessMessage(`Control detection already in progress (started ${startedAt}).`);
      return;
    }
    let deleteExisting = false;
    if (status?.controls_count > 0) {
      const confirmDelete = window.confirm(
        `This document already has ${status.controls_count} controls. Delete and re-run control detection?`
      );
      if (!confirmDelete) {
        setReprocessMessage('Reprocess canceled.');
        return;
      }
      deleteExisting = true;
    }
    setReprocessing(true);
    try {
      const formData = new FormData();
      formData.append('auditor_id', userInfo?.user_id || 'default');
      formData.append('engagement_id', engagementId);
      if (deleteExisting) {
        formData.append('delete_existing', 'true');
      }
      const resp = await fetch(`${API_BASE}/documents/${selectedDocument.id}/reprocess-controls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData
      });
      if (resp.ok) {
        setReprocessMessage('Control detection in progress. This may take a few minutes...');
        setControlDescriptionMappings([]);
        setControlDescriptionMappingState({
          status: 'stale',
          reason_code: 'SECTION_IV_REEXTRACTION_STARTED',
          reason_detail: 'Section IV extraction is running. Mapping will refresh when prerequisites are ready.'
        });
        setControlDescriptionMessage('Mapping pending while Section IV extraction runs.');
        setControlsStatus((prev) => ({
          ...prev,
          in_progress: true,
          started_at: new Date().toISOString()
        }));
        const docId = selectedDocument.id;
        startStatusPolling(docId);
      } else {
        const err = await resp.json();
        if (err.detail === 'Control detection already in progress') {
          setReprocessMessage('Control detection already in progress. Please wait.');
        } else if (err.detail === 'Controls already exist for this document') {
          setReprocessMessage('Controls already exist. Delete them before reprocessing.');
        } else {
          setError(err.detail || 'Failed to reprocess controls');
        }
      }
    } catch (e) {
      setError('Failed to reprocess controls: ' + e.message);
    } finally {
      setReprocessing(false);
    }
  };

  const handleRerunSectionIV = async () => {
    if (!selectedDocument) return;
    const confirmed = window.confirm(
      'Re-extracting Section 4: Tests of Controls will replace the current internal controls extracted for this document. Continue?'
    );
    if (!confirmed) {
      return;
    }

    setReprocessMessage('');
    setError(null);
    const status = await fetchControlsStatus(selectedDocument.id, true);
    if (status?.in_progress) {
      const startedAt = status.started_at ? new Date(status.started_at).toLocaleString() : 'now';
      setReprocessMessage(`Control detection already in progress (started ${startedAt}).`);
      return;
    }

    setReprocessing(true);
    try {
      const formData = new FormData();
      formData.append('auditor_id', userInfo?.user_id || 'default');
      formData.append('engagement_id', engagementId);
      formData.append('delete_existing', 'true');
      const resp = await fetch(`${API_BASE}/documents/${selectedDocument.id}/reprocess-controls`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
        },
        body: formData
      });
      if (resp.ok) {
        setReprocessMessage('Section IV re-extraction in progress. Existing internal controls will be replaced when the run completes.');
        setControlDescriptionMappings([]);
        setControlDescriptionMappingState({
          status: 'stale',
          reason_code: 'SECTION_IV_REEXTRACTION_STARTED',
          reason_detail: 'Section IV extraction is running. Mapping will refresh when prerequisites are ready.'
        });
        setControlDescriptionMessage('Mapping pending while Section IV extraction runs.');
        setControlsStatus((prev) => ({
          ...prev,
          in_progress: true,
          started_at: new Date().toISOString()
        }));
        startStatusPolling(selectedDocument.id);
      } else {
        const err = await resp.json();
        if (err.detail === 'Control detection already in progress') {
          setReprocessMessage('Control detection already in progress. Please wait.');
        } else {
          setError(err.detail || 'Failed to restart Section IV extraction');
        }
      }
    } catch (e) {
      setError('Failed to restart Section IV extraction: ' + e.message);
    } finally {
      setReprocessing(false);
    }
  };

  const renderSectionReviewList = (items) => {
    if (!items || items.length === 0) {
      return <div className="controls-review-empty">No review items for this section.</div>;
    }
    return (
      <div className="section-ai-review-list">
        {items.map((item, idx) => (
          <div key={`${item.page}-${idx}`} className="section-ai-review-item">
            <span className="section-ai-review-page">p. {item.page}</span>
            <span className="section-ai-review-section">{item.section_heading}</span>
            <span className="section-ai-review-flags">{(item.flags || []).join(', ')}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderSectionExtractionCard = (sectionCode, panelKey, reviewPanelKey) => {
    const canonicalSection = canonicalSectionHeadingByCode.get(sectionCode);
    const run = latestSectionRunByCode.get(sectionCode);
    const heading = canonicalSection?.display_title || canonicalSection?.report_section_title || canonicalSection?.raw_heading || canonicalSection?.canonical_section_label || canonicalSection?.heading || sectionCode;
    const pageStart = canonicalSection?.page_start || run?.page_start;
    const pageEnd = canonicalSection?.page_end || run?.page_end;
    const sectionData = run ? (sectionDataById.get(String(run.section_id)) || {}) : {};
    const rawText = sectionData.rawText || '';
    const tables = sectionData.tables || [];
    const reviewItems = groupedSectionReviewItems[sectionCode] || [];
    const hasError = Boolean(run?.error && String(run.error).trim());
    const isFailed = run?.status === 'failed' || hasError;
    const isCompleted = run?.status === 'completed' && !hasError;
    const hasSectionData = Boolean(rawText.trim()) || tables.length > 0;
    const canRerun = !sectionAIInProgress && Boolean(run) && (
      isFailed || ((isCompleted || run?.status === 'completed') && !hasSectionData)
    );
    const statusMessage = run
      ? (
        isFailed
          ? `Failed to extract the content. Error ID: ${run.error || 'Unknown error'}`
          : isCompleted
            ? 'Extraction completed.'
            : run.status === 'running'
              ? 'Extraction in progress...'
              : `Status: ${run.status || 'unknown'}`
      )
      : 'No extraction run for this section yet.';

    return (
      <div className="workflow-section-card" key={sectionCode}>
        <div className="workflow-section-topbar">
          <button type="button" className="workflow-section-toggle" onClick={() => togglePanel(panelKey)}>
            <div className="workflow-section-heading">
              <h4>{heading}</h4>
              <span className="controls-review-subtext">
                {pageStart ? `p. ${pageStart}${pageEnd && pageEnd !== pageStart ? `-${pageEnd}` : ''}` : 'Page range not set'}
              </span>
            </div>
            <span className="controls-review-toggle-icon">
              {expandedPanels[panelKey] ? '▾' : '▸'}
            </span>
          </button>
          <div className="workflow-section-actions">
            <button
              type="button"
              className="btn-rerun-controls"
              disabled={!canRerun}
              onClick={(event) => {
                event.stopPropagation();
                handleRerunSectionExtraction(sectionCode);
              }}
            >
              Re-extract Data
            </button>
          </div>
        </div>
        {expandedPanels[panelKey] && (
          <div className="workflow-section-body">
            <div className={`section-ai-run-status ${isFailed ? 'error' : isCompleted ? 'success' : 'neutral'}`}>
              {statusMessage}
            </div>
            {rawText ? (
              <div className={`section-ai-run-text ${isFailed ? 'error' : ''}`}>
                {rawText}
              </div>
            ) : (
              !tables.length && <div className="controls-review-empty">No extracted content yet.</div>
            )}
            {tables.length > 0 && (
              <div className="section-ai-run-tables">
                {tables.map((table, tableIndex) => {
                  const columns = Array.isArray(table?.columns) ? table.columns : [];
                  const rows = Array.isArray(table?.rows) ? table.rows : [];
                  const title = table?.title || `Table ${tableIndex + 1}`;
                  const pageIndex = table?.page_index ?? table?.page ?? table?.page_number;
                  const colCount = columns.length || (Array.isArray(rows[0]) ? rows[0].length : 0);
                  return (
                    <div key={`${sectionCode}-table-${tableIndex}`} className="section-ai-run-table-card">
                      <div className="section-ai-run-table-header">
                        <span className="section-ai-run-table-title">{title}</span>
                        {pageIndex ? <span className="section-ai-run-table-page">p. {pageIndex}</span> : null}
                      </div>
                      <div className="section-ai-run-table-wrap">
                        <table className="section-ai-run-table">
                          {columns.length > 0 && (
                            <thead>
                              <tr>
                                {columns.map((col, colIndex) => (
                                  <th key={`${sectionCode}-table-${tableIndex}-col-${colIndex}`}>
                                    {col || `Column ${colIndex + 1}`}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                          )}
                          <tbody>
                            {rows.length > 0 ? (
                              rows.map((row, rowIndex) => {
                                const cells = Array.isArray(row) ? row : [];
                                const filled = Array.from({ length: colCount }, (_, idx) => cells[idx] ?? '');
                                return (
                                  <tr key={`${sectionCode}-table-${tableIndex}-row-${rowIndex}`}>
                                    {filled.map((cell, cellIndex) => (
                                      <td key={`${sectionCode}-table-${tableIndex}-row-${rowIndex}-cell-${cellIndex}`}>
                                        {cell}
                                      </td>
                                    ))}
                                  </tr>
                                );
                              })
                            ) : (
                              <tr>
                                <td colSpan={Math.max(colCount, 1)} className="section-ai-run-table-empty">
                                  No rows detected.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <div className="workflow-subsection">
              <button type="button" className="workflow-subsection-toggle" onClick={() => togglePanel(reviewPanelKey)}>
                <span>Review items</span>
                <span className="controls-review-subtext">{reviewItems.length} item{reviewItems.length === 1 ? '' : 's'}</span>
                <span className="controls-review-toggle-icon">
                  {expandedPanels[reviewPanelKey] ? '▾' : '▸'}
                </span>
              </button>
              {expandedPanels[reviewPanelKey] && renderSectionReviewList(reviewItems)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderControlsPanel = () => {
    if (!selectedDocument) {
      return (
        <div className="controls-panel empty">
          <p>Select a document to view extracted controls</p>
        </div>
      );
    }

    const sectionHeadings = selectedDocument.section_headings || [];
    const orderedSectionHeadings = [...sectionHeadings].sort((a, b) => {
      const aPage = Number(a?.page_start || 0);
      const bPage = Number(b?.page_start || 0);
      if (aPage !== bPage) return aPage - bPage;
      return String(a?.display_title || a?.heading || '').localeCompare(String(b?.display_title || b?.heading || ''), undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
    const canonicalSectionHeadings = orderedSectionHeadings.filter((section) => (
      Boolean(CANONICAL_SECTION_ORDER[String(section?.canonical_section_code || '').trim().toUpperCase()])
    ));
    const otherSectionHeadings = orderedSectionHeadings.filter((section) => (
      !Boolean(CANONICAL_SECTION_ORDER[String(section?.canonical_section_code || '').trim().toUpperCase()])
    ));
    const isEditableSection = (section) => {
      const sectionId = String(section?.id || '').trim();
      return Boolean(sectionId) && !sectionId.startsWith('detected-');
    };
    const formatDetectionSource = (value) => {
      const normalized = String(value || '').trim();
      if (!normalized) return '';
      return normalized
        .split(/[_-]+/)
        .filter(Boolean)
        .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' + ');
    };
    const assignedCanonicalCodes = new Set(
      sectionHeadings
        .map((section) => String(section.canonical_section_code || '').trim().toUpperCase())
        .filter(Boolean)
    );
    const availableSectionOptionsForAdd = CANONICAL_SECTION_OPTIONS.filter(
      (option) => !assignedCanonicalCodes.has(option.code)
    );
    const getAvailableSectionOptionsForEdit = () => CANONICAL_SECTION_OPTIONS;
    const handleDeleteSection = async (sectionId) => {
      try {
        const resp = await fetch(`${API_BASE}/documents/${selectedDocument.id}/sections/${sectionId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          },
        });
        if (resp.ok) {
          // Optimistically update UI without full reload
          const updatedHeadings = sectionHeadings.filter(s => s.id !== sectionId);
          setSelectedDocument(prev => prev && prev.id === selectedDocument.id
            ? { ...prev, section_headings: updatedHeadings, total_sections: updatedHeadings.length }
            : prev
          );
          setDocuments(prev => prev.map(doc => 
            doc.id === selectedDocument.id
              ? { ...doc, section_headings: updatedHeadings, total_sections: updatedHeadings.length }
              : doc
          ));
          setEditingSection(null);
          // Sync with backend in background and keep selection if still present
          loadDocumentsCallback();
        }
      } catch (e) {
        console.error('Failed to delete section', e);
      }
    };

    const handleEditSection = (section) => {
      setAddingSection(null);
      setEditingSection({
        id: section.id,
        canonical_section_code: section.canonical_section_code || '',
        page_start: section.page_start,
        page_end: section.page_end || ''
      });
    };

    const handleAddSection = () => {
      setEditingSection(null);
      setAddingSection({
        canonical_section_code: availableSectionOptionsForAdd[0]?.code || '',
        page_start: '',
        page_end: ''
      });
    };

    const handleSubmitSectionAdd = async (e) => {
      e.preventDefault();
      if (!addingSection || !selectedDocument) return;
      const { canonical_section_code, page_start, page_end } = addingSection;
      try {
        const formData = new FormData();
        formData.append('auditor_id', userInfo?.user_id || 'default');
        formData.append('engagement_id', engagementId);
        formData.append('canonical_section_code', canonical_section_code);
        formData.append('page_start', page_start);
        if (page_end !== '') {
          formData.append('page_end', page_end);
        }
        const resp = await fetch(`${API_BASE}/documents/${selectedDocument.id}/sections`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          },
          body: formData
        });
        if (resp.ok) {
          await loadDocumentsCallback();
          await loadDocumentDetails(selectedDocument.id);
          setAddingSection(null);
        } else {
          const err = await resp.json();
          setError(err.detail || 'Failed to add section');
        }
      } catch (e) {
        setError('Failed to add section: ' + e.message);
      }
    };

    const handleSubmitSectionEdit = async (e) => {
      e.preventDefault();
      if (!editingSection) return;
      const { id, canonical_section_code, page_start, page_end } = editingSection;
      try {
        const formData = new FormData();
        if (canonical_section_code) {
          formData.append('canonical_section_code', canonical_section_code);
        }
        formData.append('page_start', page_start);
        if (page_end !== '') {
          formData.append('page_end', page_end);
        }
        const resp = await fetch(`${API_BASE}/documents/${selectedDocument.id}/sections/${id}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('session_token')}`,
          },
          body: formData
        });
        if (resp.ok) {
          await loadDocumentsCallback();
          await loadDocumentDetails(selectedDocument.id);
          setEditingSection(null);
        }
      } catch (e) {
        console.error('Failed to edit section', e);
      }
    };

    return (
      <div className="controls-panel">
        <div className="panel-header">
          <div className="panel-header-main">
            <h3>Document Review</h3>
            <span className="control-count">{selectedDocument.filename}</span>
          </div>
        </div>

        <div className="workflow-action-bar">
          <button 
            className="btn-validate"
            onClick={() => handleValidate(selectedDocument.id)}
            disabled={selectedDocument.validation_status !== 'DRAFT'}
          >
            ✅ Accept Document
          </button>
          <button 
            className="btn-reject"
            onClick={() => handleReject(selectedDocument.id)}
            disabled={selectedDocument.validation_status !== 'DRAFT'}
          >
            ❌ Reject Document
          </button>
          {intakeV4Available && (
            <button
              className="btn-map-description"
              type="button"
              onClick={handleRunIntakeV4}
              disabled={intakeV4Loading || intakeV4Running}
            >
              {intakeV4Loading || intakeV4Running ? '⏳ Running Extraction...' : '▶ Run Extraction'}
            </button>
          )}
        </div>
        {(reprocessMessage || sectionExtractionMessage || intakeV4Message || intakeV4Error) && (
          <div className="workflow-status-stack">
            {reprocessMessage && <div className="reprocess-message">{reprocessMessage}</div>}
            {sectionExtractionMessage && <div className="reprocess-message">{sectionExtractionMessage}</div>}
            {intakeV4Message && <div className="controls-review-message">{intakeV4Message}</div>}
            {intakeV4Error && <div className="controls-review-warning">{intakeV4Error}</div>}
          </div>
        )}
        {intakeV4Run && (
          <div className="workflow-run-meta">
            <span className="controls-review-subtext">Run ID: {intakeV4RunId || '—'}</span>
            <div className="workflow-stage-strip">
              {intakeV4StageStatuses.map((item) => (
                <div key={item.label} className="workflow-stage-pill">
                  <span>{item.label}</span>
                  <span className={`status-pill ${statusPillClassName(item.value || 'idle')}`}>
                    {item.value || '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
        {(intakeV4BatchProgress.total > 0 || intakeV4BatchProgress.completed > 0 || intakeV4BatchProgress.failed > 0) && (
          <div className="intake-v4-batch-progress">
            <span className="controls-review-subtext">
              Section III batches
              {intakeV4BatchProgress.attemptNo ? ` · attempt ${intakeV4BatchProgress.attemptNo}` : ''}
              {intakeV4BatchProgress.method ? ` · ${intakeV4BatchProgress.method}` : ''}
              {`: ${intakeV4BatchProgress.completed}/${intakeV4BatchProgress.total || '—'} completed`}
              {intakeV4BatchProgress.inProgress > 0 ? ` · ${intakeV4BatchProgress.inProgress} in progress` : ''}
              {intakeV4BatchProgress.failed > 0 ? ` · ${intakeV4BatchProgress.failed} failed` : ''}
            </span>
          </div>
        )}

        <div className="section-list">
          <div className="section-list-header-row">
            <div>
              <div className="section-list-header">Resolved sections</div>
              <div className="section-list-subheader">Detector-backed ranges used by the app</div>
            </div>
            <button
              className="section-add-button"
              onClick={handleAddSection}
              disabled={availableSectionOptionsForAdd.length === 0}
            >
              + Add Section
            </button>
          </div>
          {addingSection && (
            <form className="section-edit-form" onSubmit={handleSubmitSectionAdd}>
              <select
                value={addingSection.canonical_section_code}
                onChange={(e) => setAddingSection({ ...addingSection, canonical_section_code: e.target.value })}
                required
              >
                {availableSectionOptionsForAdd.map((option) => (
                  <option key={option.code} value={option.code}>{option.label}</option>
                ))}
              </select>
              <input
                type="number"
                value={addingSection.page_start}
                onChange={(e) => setAddingSection({ ...addingSection, page_start: e.target.value })}
                placeholder="Start page"
                required
                min="1"
              />
              <input
                type="number"
                value={addingSection.page_end}
                onChange={(e) => setAddingSection({ ...addingSection, page_end: e.target.value })}
                placeholder="End page (optional)"
                min="1"
              />
              <div className="section-edit-actions">
                <button type="submit">Add</button>
                <button type="button" onClick={() => setAddingSection(null)}>Cancel</button>
              </div>
            </form>
          )}
          {editingSection && (
            <form className="section-edit-form" onSubmit={handleSubmitSectionEdit}>
              <select
                value={editingSection.canonical_section_code}
                onChange={(e) => setEditingSection({ ...editingSection, canonical_section_code: e.target.value })}
                required
              >
                {getAvailableSectionOptionsForEdit().map((option) => (
                  <option key={option.code} value={option.code}>{option.label}</option>
                ))}
              </select>
              <input
                type="number"
                value={editingSection.page_start}
                onChange={(e) => setEditingSection({ ...editingSection, page_start: e.target.value })}
                placeholder="Start page"
                required
                min="1"
              />
              <input
                type="number"
                value={editingSection.page_end}
                onChange={(e) => setEditingSection({ ...editingSection, page_end: e.target.value })}
                placeholder="End page (optional)"
                min="1"
              />
              <div className="section-edit-actions">
                <button type="submit">Save</button>
                <button type="button" onClick={() => setEditingSection(null)}>Cancel</button>
              </div>
            </form>
          )}
          {orderedSectionHeadings.length === 0 ? (
            <div className="no-controls">No section headings detected.</div>
          ) : (
            <>
              {canonicalSectionHeadings.length > 0 && (
                <div className="section-group">
                  {canonicalSectionHeadings.map((s, idx) => {
                    const editable = isEditableSection(s);
                    const semanticLabel = CANONICAL_SECTION_LABELS[String(s?.canonical_section_code || '').trim().toUpperCase()] || s.canonical_section_label;
                    const displayLabel = s.is_manual_override && semanticLabel
                      ? semanticLabel
                      : (s.display_title || s.report_section_title || s.raw_heading || s.canonical_section_label || s.heading);
                    const detectionMeta = [];
                    if (s.detection_source) detectionMeta.push(formatDetectionSource(s.detection_source));
                    if (typeof s.confidence === 'number') detectionMeta.push(`Confidence ${Number(s.confidence).toFixed(2)}`);
                    return (
                      <div key={`${s.id || s.canonical_section_code || 'canonical'}-${idx}`} className="section-item section-item-primary">
                        <div className="section-main">
                          <div className="section-title-row">
                            <div className="section-title">{displayLabel}</div>
                            <span className="section-kind-badge">Semantic</span>
                          </div>
                          {semanticLabel && semanticLabel !== displayLabel && (
                            <div className="section-subtitle">Semantic type: {semanticLabel}</div>
                          )}
                          {detectionMeta.length > 0 && (
                            <div className="section-meta">{detectionMeta.join(' · ')}</div>
                          )}
                        </div>
                        <div className="section-page">p. {s.page_start}{s.page_end && s.page_end !== s.page_start ? `-${s.page_end}` : ''}</div>
                        <div className="section-actions">
                          {editable ? (
                            <>
                              <button onClick={() => handleEditSection(s)}>Edit</button>
                              <button onClick={() => handleDeleteSection(s.id)}>Delete</button>
                            </>
                          ) : (
                            <span className="section-readonly-note">Auto-resolved</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              {otherSectionHeadings.length > 0 && (
                <div className="section-group section-group-secondary">
                  <div className="section-subgroup-header">Other detected headings</div>
                  {otherSectionHeadings.map((s, idx) => {
                    const editable = isEditableSection(s);
                    return (
                      <div key={`${s.id || s.heading || 'other'}-${idx}`} className="section-item section-item-secondary">
                        <div className="section-main">
                          <div className="section-title-row">
                            <div className="section-title">{s.heading || s.raw_heading || 'Untitled heading'}</div>
                            <span className="section-kind-badge section-kind-badge-secondary">Other</span>
                          </div>
                          {s.raw_heading && s.raw_heading !== (s.heading || '') && (
                            <div className="section-subtitle">{s.raw_heading}</div>
                          )}
                        </div>
                        <div className="section-page">p. {s.page_start}{s.page_end && s.page_end !== s.page_start ? `-${s.page_end}` : ''}</div>
                        <div className="section-actions">
                          {editable ? (
                            <>
                              <button onClick={() => handleEditSection(s)}>Edit</button>
                              <button onClick={() => handleDeleteSection(s.id)}>Delete</button>
                            </>
                          ) : (
                            <span className="section-readonly-note">Reference only</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

      </div>
    );
  };

  return (
    <div className={`document-validation theme-${themeMode}`}>
      {/* Header */}
      <div className="validation-header">
        <div className="header-left">
          <button className="back-button" onClick={onBack}>
            ← Back
          </button>
          <div>
            <h1>Document Validation</h1>
            <p>Phase 1.5 - Review and validate parsed documents</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            type="button"
            className="theme-toggle"
            onClick={() => setThemeMode((prev) => (prev === 'dark' ? 'light' : 'dark'))}
            aria-label={`Switch to ${themeMode === 'dark' ? 'light' : 'dark'} mode`}
          >
            {themeMode === 'dark' ? '☀ Light' : '☾ Dark'}
          </button>
        </div>
      </div>

      {/* Planning Readiness Banner */}
      {planningReadiness && (
        <div className={`readiness-banner ${planningReadiness.ready ? 'ready' : 'not-ready'}`}>
          <div className="readiness-info">
            {planningReadiness.ready ? (
              <>
                <span className="readiness-icon">✅</span>
                <span>Ready for Planning Phase - All required documents validated</span>
              </>
            ) : (
              <>
                <span className="readiness-icon">⏳</span>
                <span>
                  Not ready for Planning - {planningReadiness.validated_count}/{planningReadiness.required_count} checks passed
                  {planningReadiness.missing_documents?.length > 0 && (
                    <span className="missing-docs">
                      Missing: {planningReadiness.missing_documents.join(', ')}
                    </span>
                  )}
                </span>
              </>
            )}
          </div>
          {planningReadiness.ready && (
            <button
              className="btn-move-planning"
              onClick={handleMoveToPlanning}
              disabled={transitioning}
            >
              {transitioning ? 'Moving...' : 'Move to Planning →'}
            </button>
          )}
        </div>
      )}

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="modal-overlay">
          <div className="modal-content upload-modal">
            <h2>Upload Documents</h2>
            <p>Selected files:</p>
            <ul className="file-list">
              {uploadFiles.map((file, idx) => (
                <li key={idx}>📄 {file.name} ({(file.size / 1024).toFixed(1)} KB)</li>
              ))}
            </ul>
            <div className="modal-actions">
              <button 
                onClick={() => { setShowUploadModal(false); setUploadFiles([]); }}
                disabled={uploading}
              >
                Cancel
              </button>
              <button 
                className="btn-primary"
                onClick={handleUpload}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload'}
              </button>
            </div>
          </div>
        </div>
      )}

      {editingControl && (
        <div className="modal-overlay">
          <div className="modal-content control-edit-modal">
            <h2>Edit Control</h2>
            <form onSubmit={handleSubmitControlEdit} className="control-edit-form">
              <label>
                Control ID
                <input
                  type="text"
                  value={editingControl.control_id}
                  onChange={(e) => setEditingControl({ ...editingControl, control_id: e.target.value })}
                />
              </label>
              <label>
                Description
                <textarea
                  value={editingControl.description}
                  onChange={(e) => setEditingControl({ ...editingControl, description: e.target.value })}
                  rows={4}
                />
              </label>
              <label>
                TSC Control ID
                <input
                  type="text"
                  value={editingControl.tsc_control_id}
                  onChange={(e) => setEditingControl({ ...editingControl, tsc_control_id: e.target.value })}
                />
              </label>
              <label>
                TSC Control Name
                <input
                  type="text"
                  value={editingControl.tsc_control_name}
                  onChange={(e) => setEditingControl({ ...editingControl, tsc_control_name: e.target.value })}
                />
              </label>
              <label>
                Test Result
                <textarea
                  value={editingControl.test_result}
                  onChange={(e) => setEditingControl({ ...editingControl, test_result: e.target.value })}
                  rows={3}
                />
              </label>
              <label>
                Source Page
                <input
                  type="number"
                  min="1"
                  value={editingControl.source_page}
                  onChange={(e) => setEditingControl({ ...editingControl, source_page: e.target.value })}
                />
              </label>
              <label>
                Reason Code
                <select
                  value={editingControl.reason_code || ''}
                  onChange={(e) => setEditingControl({ ...editingControl, reason_code: e.target.value })}
                >
                  <option value="">Select reason</option>
                  {CONTROL_CORRECTION_REASON_OPTIONS.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <div className="modal-actions">
                <button type="button" onClick={() => setEditingControl(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {editingMapping && (
        <div className="modal-overlay">
          <div className="modal-content control-edit-modal mapping-edit-modal">
            <h2>Edit Mapping</h2>
            <form onSubmit={handleSubmitMappingEdit} className="control-edit-form">
              <label>
                Internal Control ID
                <input type="text" value={editingMapping.control_id} readOnly />
              </label>
              <label>
                Internal Control Statement
                <textarea
                  className="mapping-readonly-statement"
                  value={editingMapping.internal_control_statement}
                  rows={3}
                  readOnly
                />
              </label>
              <label>
                Selected Sentence IDs
                <input
                  type="text"
                  value={editingMapping.selected_sentence_ids}
                  onChange={(e) => setEditingMapping({ ...editingMapping, selected_sentence_ids: e.target.value })}
                  placeholder="S1, S2"
                />
              </label>
              <label>
                Primary Sentence ID
                <select
                  value={editingMapping.primary_sentence_id || ''}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    const sentence = mappingSentenceOptions.find((entry) => String(entry.sentence_id) === nextId);
                    const selectedIds = (editingMapping.selected_sentence_ids || '')
                      .split(',')
                      .map((value) => value.trim())
                      .filter(Boolean);
                    const merged = nextId
                      ? Array.from(new Set([nextId, ...selectedIds]))
                      : selectedIds;
                    setEditingMapping({
                      ...editingMapping,
                      primary_sentence_id: nextId,
                      selected_sentence_ids: merged.join(', '),
                      mapped_text: sentence?.sentence_text || editingMapping.mapped_text,
                    });
                  }}
                >
                  <option value="">Select sentence</option>
                  {mappingSentenceOptions.map((entry) => (
                    <option key={entry.sentence_id} value={entry.sentence_id}>
                      {entry.sentence_id} · p{entry.page_number} · {(entry.sentence_text || '').slice(0, 90)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Mapped Sentence Text
                <textarea
                  value={editingMapping.mapped_text}
                  onChange={(e) => setEditingMapping({ ...editingMapping, mapped_text: e.target.value })}
                  rows={4}
                />
              </label>
              <label>
                Mapping Confidence
                <input
                  type="number"
                  min="0"
                  max="1"
                  step="0.01"
                  value={editingMapping.mapping_confidence}
                  onChange={(e) => setEditingMapping({ ...editingMapping, mapping_confidence: e.target.value })}
                />
              </label>
              <label className="mapping-edit-checkbox">
                <input
                  type="checkbox"
                  checked={Boolean(editingMapping.is_accepted)}
                  onChange={(e) => setEditingMapping({ ...editingMapping, is_accepted: e.target.checked })}
                />
                <span>Mark mapping as accepted</span>
              </label>
              <label>
                Reason Code
                <select
                  value={editingMapping.reason_code || ''}
                  onChange={(e) => setEditingMapping({ ...editingMapping, reason_code: e.target.value })}
                >
                  <option value="">Select reason</option>
                  {MAPPING_CORRECTION_REASON_OPTIONS.map((option) => (
                    <option key={option.code} value={option.code}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Reason Description
                <textarea
                  value={editingMapping.reason_description || ''}
                  onChange={(e) => setEditingMapping({ ...editingMapping, reason_description: e.target.value })}
                  rows={4}
                  placeholder="Describe why the mapping is incorrect and what the reviewer concluded."
                />
              </label>
              {sectionSentenceCatalogLoading && (
                <div className="controls-review-empty">Loading Section III sentences...</div>
              )}
              <div className="modal-actions">
                <button type="button" onClick={() => setEditingMapping(null)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Error Banner */}
      {error && (
        <div className="error-banner">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Main Content */}
      <div className="validation-content">
        {/* Documents List */}
        <div className="documents-section">
          <h3>Uploaded Documents</h3>
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            multiple
            accept=".pdf,.doc,.docx,.xlsx,.xls,.csv,.txt"
            style={{ display: 'none' }}
          />
          
          {loading ? (
            <div className="loading">Loading documents...</div>
          ) : documents.length === 0 ? (
            <div className="empty-state">
              <p>No documents uploaded yet.</p>
              <button 
                className="btn-upload"
                onClick={() => fileInputRef.current?.click()}
              >
                Upload Documents
              </button>
            </div>
          ) : (
            <div className="documents-list">
              {documents.map(renderDocumentCard)}
              <button 
                className="btn-upload-more"
                onClick={() => fileInputRef.current?.click()}
              >
                + Upload More
              </button>
            </div>
          )}
        </div>

        {/* Controls Panel */}
        {renderControlsPanel()}
      </div>

      {selectedDocument && (
        <div className="workflow-stack">
          <div className="workflow-group-header">
            <div>
              <h3>Sections I–III</h3>
              <span className="controls-review-subtext">Expand a section to review extracted content and related review items.</span>
            </div>
          </div>
          {orderedSectionWorkflowCodes.map((sectionCode) => {
            const panelMap = {
              SECTION_I: ['sectionI', 'sectionIReview'],
              SECTION_II: ['sectionII', 'sectionIIReview'],
              SECTION_III: ['sectionIII', 'sectionIIIReview'],
            };
            const [panelKey, reviewPanelKey] = panelMap[sectionCode] || [sectionCode, `${sectionCode}Review`];
            return renderSectionExtractionCard(sectionCode, panelKey, reviewPanelKey);
          })}

          <div className="workflow-section-card">
            <div className="workflow-section-topbar">
              <button
                type="button"
                className="workflow-section-toggle"
                onClick={() => setControlsExpanded((prev) => !prev)}
              >
                <div className="workflow-section-heading">
                  <h4>Section IV: Tests of Controls</h4>
                  <span className="controls-review-subtext">
                    {controlsTotal} controls
                    {controlsNeedsReviewCount > 0 ? (
                      <span className="controls-review-flag controls-review-flag-warning">
                        {' '}· {controlsNeedsReviewCount} needs review
                      </span>
                    ) : null}
                    {controlsRejectedCount > 0 ? (
                      <span className="controls-review-flag controls-review-flag-danger">
                        {' '}· {controlsRejectedCount} rejected
                      </span>
                    ) : null}
                    {notDetectedCount > 0 ? (
                      <span className="controls-review-flag">
                        {' '}· {notDetectedCount} TSC Not Detected
                      </span>
                    ) : null}
                  </span>
                </div>
                <span className="controls-review-toggle-icon">
                  {controlsExpanded ? '▾' : '▸'}
                </span>
              </button>
              <div className="workflow-section-actions">
                <button
                  type="button"
                  className="btn-rerun-controls"
                  disabled={!canRerunSectionIV || reprocessing}
                  onClick={handleRerunSectionIV}
                >
                  Re-extract Data
                </button>
              </div>
            </div>
            {controlsExpanded && (
              <div className="workflow-section-body">
                {(!controlsStatus.in_progress && controlsStatus.last_run_status && (
                  controlsStatus.last_run_status === 'failed' ||
                  controlsTotal === 0
                )) && (
                  <div className="controls-review-warning">
                    Internal Controls extraction failed, please re-create section and try again.
                  </div>
                )}
                {rerunMessage && (
                  <div className="controls-review-message">{rerunMessage}</div>
                )}
                {groupedControls.length === 0 ? (
                  <div className="controls-review-empty">No controls extracted for this document yet.</div>
                ) : (
                  groupedControls.map((group) => {
                    const isExpanded = expandedControlGroups[group.key] !== false;
                    return (
                      <div key={group.key} className="controls-group">
                        <button
                          className="controls-group-toggle"
                          onClick={() => toggleControlGroup(group.key)}
                          type="button"
                        >
                          <span>{group.label}</span>
                          <span className="controls-group-count">
                            {group.items.length} {group.items.length === 1 ? 'control' : 'controls'}
                          </span>
                        </button>
                        {isExpanded && (
                          <div className="controls-group-body">
                            <div className="controls-table-header">
                              <span>Select</span>
                              <span>Control ID</span>
                              <span>Description</span>
                              <span>TSC ID</span>
                              <span>TSC Name</span>
                              <span>Test result</span>
                              <span>Page</span>
                              <span>Confidence</span>
                              <span>Notes / Status</span>
                              <span>Status</span>
                              <span>Action</span>
                            </div>
                            {group.items.map((ctrl) => {
                              const extractionStatus = getControlExtractionStatus(ctrl);
                              const notesHasWarning = extractionStatus !== 'valid'
                                || (Array.isArray(ctrl.failure_labels) && ctrl.failure_labels.length > 0)
                                || (Array.isArray(ctrl.quality_flags_json) && ctrl.quality_flags_json.length > 0);
                              return (
                                <div key={ctrl.id} className={`controls-table-row extraction-${extractionStatus}`}>
                                  <input
                                    type="checkbox"
                                    checked={selectedControlIds.has(ctrl.id)}
                                    onChange={() => toggleControlSelection(ctrl.id)}
                                  />
                                  {renderControlCell(ctrl.control_id, 'control-id')}
                                  {renderControlCell(ctrl.description)}
                                  {renderControlCell(
                                    ctrl.tsc_control_id,
                                    isSameValue(ctrl.control_id, ctrl.tsc_control_id) ? 'match-warning' : ''
                                  )}
                                  {renderControlCell(
                                    ctrl.tsc_control_name,
                                    isSameValue(ctrl.description, ctrl.tsc_control_name) ? 'match-warning' : ''
                                  )}
                                  {renderControlCell(ctrl.test_result)}
                                  {renderControlCell(ctrl.source_page)}
                                  {renderControlCell(formatControlConfidence(ctrl))}
                                  {renderControlCell(formatControlNotes(ctrl), notesHasWarning ? 'match-warning' : '')}
                                  {renderControlExtractionStatus(ctrl)}
                                  <button
                                    className="control-edit-button"
                                    type="button"
                                    onClick={() => handleEditControl(ctrl)}
                                  >
                                    Edit
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>

          <div className="workflow-section-card">
            <div className="workflow-section-topbar">
              <button
                type="button"
                className="workflow-section-toggle"
                onClick={() => setControlDescriptionExpanded((prev) => !prev)}
              >
                <div className="workflow-section-heading">
                  <h4>Internal Control to System Description Mapping</h4>
                  <span className="controls-review-subtext">
                    {(controlDescriptionSummary.processed_controls ?? controlDescriptionMappings.length)} controls processed
                    {' '}· {(controlDescriptionSummary.mapped_controls ?? (controlDescriptionMappings.length - unmappedControlDescriptionCount))} controls mapped
                  </span>
                </div>
                <span className="controls-review-toggle-icon">
                  {controlDescriptionExpanded ? '▾' : '▸'}
                </span>
              </button>
              <div className="workflow-section-actions">
                <button
                  type="button"
                  className="btn-rerun-controls"
                  disabled={!canRerunMapping}
                  onClick={handleRerunControlDescriptionMapping}
                >
                  Re-extract Data
                </button>
              </div>
            </div>
            {controlDescriptionExpanded && (
              <div className="workflow-section-body">
                {controlDescriptionError && (
                  <div className="controls-review-warning">{controlDescriptionError}</div>
                )}
                {controlDescriptionMessage && (
                  <div className="controls-review-message">{controlDescriptionMessage}</div>
                )}
                {mappingDependencyStatus && (
                  <div className="controls-review-subtext">
                    Mapping dependency status: {mappingDependencyStatusLabel}
                  </div>
                )}
                {(controlDescriptionSummary.not_mapped_controls_excluding_failed
                  ?? controlDescriptionSummary.unmapped_controls
                  ?? unmappedControlDescriptionCount) > 0 && (
                  <div className="controls-review-subtext controls-review-unmapped">
                    {(controlDescriptionSummary.not_mapped_controls_excluding_failed
                      ?? controlDescriptionSummary.unmapped_controls
                      ?? unmappedControlDescriptionCount)} controls are not mapped
                    {(controlDescriptionSummary.failed_controls ?? 0) > 0
                      ? ` · ${controlDescriptionSummary.failed_controls} failed to map`
                      : ''}
                    {(controlDescriptionSummary.orphan_reference_controls ?? 0) > 0
                      ? ` · ${controlDescriptionSummary.orphan_reference_controls} orphan reference`
                      : ''}
                  </div>
                )}
                {controlDescriptionLoading ? (
                  <div className="controls-review-empty">Loading mapping...</div>
                ) : controlDescriptionMappings.length === 0 ? (
                  <div className="controls-review-empty">No mapping rows yet.</div>
                ) : (
                  <div className="controls-group">
                    <div className="controls-group-body">
                      <div className="controls-table-header control-description-grid-header">
                        <span>Internal Control ID</span>
                        <span>Internal Control Statement</span>
                        <span>Mapped Paragraph Statement</span>
                        <span>Sentence #</span>
                        <span>AI Confidence</span>
                        <span>Status / Notes</span>
                        <span>Action</span>
                      </div>
                      {sortedControlDescriptionMappings.map((row) => (
                        <div key={row.id} className="controls-table-row control-description-grid-row">
                          {renderControlCell(row.internal_control_id_final || row.internal_control_id_ai)}
                          {renderControlCell(row.internal_control_statement_final || row.internal_control_statement_ai)}
                          {renderControlCell(row.mapped_paragraph_text_final || row.mapped_text_final || row.mapped_text_ai)}
                          {renderControlCell(row.paragraph_id_final || row.primary_sentence_id_final || row.primary_sentence_id_ai || row.paragraph_index_final)}
                          {renderControlCell(formatMappingConfidence(row))}
                          {renderControlCell(formatMappingNotes(row), getMappingNoteClass(row))}
                          <button
                            className="control-edit-button"
                            type="button"
                            onClick={() => handleEditMapping(row)}
                          >
                            Edit
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="workflow-section-card">
            <button
              type="button"
              className="workflow-section-toggle"
              onClick={() => togglePanel('personnel')}
            >
              <div className="workflow-section-heading">
                <h4>Personnel detected in the report</h4>
                <span className="controls-review-subtext">{personnelEntries.length} entries</span>
              </div>
              <span className="controls-review-toggle-icon">
                {expandedPanels.personnel ? '▾' : '▸'}
              </span>
            </button>
            {expandedPanels.personnel && (
              <div className="workflow-section-body">
                {personnelError && (
                  <div className="personnel-review-error">{personnelError}</div>
                )}
                {!personnelLoading && shouldShowPersonnelStatusNotice() && (
                  <div className="controls-review-empty">{getPersonnelStatusMessage()}</div>
                )}
                <div className="personnel-review-form">
                  <input
                    type="text"
                    placeholder="Full name"
                    value={newPersonnel.full_name}
                    onChange={(e) => setNewPersonnel({ ...newPersonnel, full_name: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Position/title"
                    value={newPersonnel.position_title}
                    onChange={(e) => setNewPersonnel({ ...newPersonnel, position_title: e.target.value })}
                  />
                  <input
                    type="number"
                    placeholder="Page start"
                    min="1"
                    value={newPersonnel.page_start}
                    onChange={(e) => setNewPersonnel({ ...newPersonnel, page_start: e.target.value })}
                  />
                  <input
                    type="number"
                    placeholder="Page end"
                    min="1"
                    value={newPersonnel.page_end}
                    onChange={(e) => setNewPersonnel({ ...newPersonnel, page_end: e.target.value })}
                  />
                  <input
                    type="text"
                    placeholder="Org unit (optional)"
                    value={newPersonnel.org_unit}
                    onChange={(e) => setNewPersonnel({ ...newPersonnel, org_unit: e.target.value })}
                  />
                  <button type="button" onClick={handleAddPersonnel}>Add</button>
                </div>
                {personnelLoading ? (
                  <div className="controls-review-empty">Loading personnel...</div>
                ) : personnelEntries.length === 0 ? (
                  <div className="controls-review-empty">
                    {shouldShowPersonnelStatusNotice()
                      ? 'Add personnel manually if needed.'
                      : getPersonnelStatusMessage()}
                  </div>
                ) : (
                  <div className="personnel-review-list">
                    {personnelEntries.map((entry) => {
                      const isEditing = editingPersonnelId === entry.id;
                      return (
                        <div key={entry.id} className="personnel-review-row">
                          {isEditing ? (
                            <>
                              <input
                                type="text"
                                value={editingPersonnel.full_name}
                                onChange={(e) => setEditingPersonnel({ ...editingPersonnel, full_name: e.target.value })}
                              />
                              <input
                                type="text"
                                value={editingPersonnel.position_title}
                                onChange={(e) => setEditingPersonnel({ ...editingPersonnel, position_title: e.target.value })}
                              />
                              <input
                                type="number"
                                min="1"
                                value={editingPersonnel.page_start}
                                onChange={(e) => setEditingPersonnel({ ...editingPersonnel, page_start: e.target.value })}
                              />
                              <input
                                type="number"
                                min="1"
                                value={editingPersonnel.page_end}
                                onChange={(e) => setEditingPersonnel({ ...editingPersonnel, page_end: e.target.value })}
                              />
                              <input
                                type="text"
                                value={editingPersonnel.org_unit}
                                onChange={(e) => setEditingPersonnel({ ...editingPersonnel, org_unit: e.target.value })}
                              />
                              <div className="personnel-review-actions">
                                <button type="button" onClick={() => handleSavePersonnelEdit(entry.id)}>Save</button>
                                <button type="button" onClick={cancelEditPersonnel}>Cancel</button>
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="personnel-review-main">
                                <div className="personnel-review-name">{entry.full_name}</div>
                                <div className="personnel-review-title">{entry.position_title}</div>
                                <div className="personnel-review-meta">
                                  {entry.page_start ? `p. ${entry.page_start}${entry.page_end && entry.page_end !== entry.page_start ? `-${entry.page_end}` : ''}` : 'Page n/a'}
                                </div>
                                {entry.source_type && (
                                  <div className="personnel-review-source">{entry.source_type}</div>
                                )}
                              </div>
                              <div className="personnel-review-actions">
                                <button type="button" onClick={() => startEditPersonnel(entry)}>Edit</button>
                                <button type="button" onClick={() => handleDeletePersonnel(entry.id)}>Delete</button>
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="workflow-section-card">
            <button
              type="button"
              className="workflow-section-toggle"
              onClick={() => togglePanel('escalations')}
            >
              <div className="workflow-section-heading">
                <h4>Escalations</h4>
                <span className="controls-review-subtext">{intakeV4Escalations.length} item{intakeV4Escalations.length === 1 ? '' : 's'}</span>
              </div>
              <span className="controls-review-toggle-icon">
                {expandedPanels.escalations ? '▾' : '▸'}
              </span>
            </button>
            {expandedPanels.escalations && (
              <div className="workflow-section-body">
                {!intakeV4Available ? (
                  <div className="controls-review-empty">Intake v4 is disabled in this environment.</div>
                ) : intakeV4Escalations.length === 0 ? (
                  <div className="controls-review-empty">No escalation items.</div>
                ) : (
                  <div className="section-ai-review-list">
                    {intakeV4Escalations.map((item, idx) => (
                      <div key={`${item.code || 'code'}-${idx}`} className="section-ai-review-item intake-v4-escalation-row">
                        <span className="section-ai-review-page">{item.section || 'SECTION'}</span>
                        <span className={`status-pill ${statusPillClassName(item.severity || 'warning')}`}>
                          {item.severity || 'warning'}
                        </span>
                        <span className="section-ai-review-flags">
                          {item.summary}
                          {Number(item.count || 0) > 0 ? ` (${item.count})` : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default DocumentValidation;
